// @ts-strict-ignore

/**
 * @module Bubbles
 */

import { AccessRequestStatus, Bubble, BubbleCleanupData, BubbleEvents, BubbleInvitation, BubbleRB, BubbleSettings, BubbleUser, BubbleUserRole, BubbleUserStatus, BubbleVisibility, ConferenceRecordingConfig, LobbyUser } from 'rainbow-web-sdk/models/bubble.model';
import { Subject, Subscription, filter, interval } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import anonymizer from 'rainbow-web-sdk/utils/anonymizer';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { getBinaryData, resizeImage } from 'rainbow-web-sdk/services/core/imageUtils.service';

import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import type { FileStorageService } from 'rainbow-web-sdk/services/files/fileStorage.service';
import { FILE_STORAGE_SVC } from 'rainbow-web-sdk/services/files/fileStorage.service';
import { BUBBLE_CONFERENCE_SVC, BubbleConferenceServiceEvents } from '../conference/bubbleConference.service';
import type { BubbleConferenceServiceRB } from '../conference/bubbleConference.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { I18nService } from 'rainbow-web-sdk/services/core/i18n.service';
import randomString from 'rainbow-web-sdk/utils/randomString';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { ErrorHelperInfo, ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { DialInPhoneNumber } from 'rainbow-web-sdk/models/dial-in-phone-numbers.model';
import { Service } from 'rainbow-web-sdk/services/service';
import xmlParser, { XMLElement } from 'rainbow-web-sdk/utils/xmlParser';
import { allSettled } from 'rainbow-web-sdk';
import { User } from 'rainbow-web-sdk/models';
import { CallRB, CallType } from 'rainbow-web-sdk/models/call.model';
import { BubbleConferenceStatus, WebConferenceSession } from 'rainbow-web-sdk/models/webConferenceSession.model';
import { PromiseQueue } from '../../utils/promiseQueue';
import { BUBBLE_TAG_SVC, BubbleTagService } from './bubbleTag.service';
import { MD5, UtilitiesService } from '../utilities/utilities.service';
import { $pres } from 'strophe.js';
import type { StropheHandler } from 'rainbow-web-sdk';

/** @internal */
export const BUBBLE_SVC = 'BubbleService';

/**
 * @eventProperty
 * This enum lists all the `RBEvent` events generated by the BubbleService.
 *
 * They can be listened to using the `bubbleService.subscribe()` API.
 */
export enum BubbleServiceEvents {

    /**
     * @eventProperty
     * This `RBEvent` is send when the bubble service is started and ready to be used (also fired after loss of network and reconnection)
     */
    ON_SERVICE_STARTED = 'ON_SERVICE_STARTED',

    /**
     * @eventProperty
     * This RBEvent is send when a new bubble is created
     * **Event Data**
     * - **bubble**: The new created bubble
     * - **invitedUsersErrors?**: Status indicating whether some/all of the invited users have not successfully been added when creating the new bubble.
     *   This information is relevant only for bubbles/rooms created by a user.
     * - **invalidUsersIds?**: The Id(s) of the contacts that could not be invited to the bubble
     */
    ON_BUBBLE_CREATED = 'ON_BUBBLE_CREATED',

    /**
     * @eventProperty
     * This RBEvent is send when a bubble has been deleted by the owner.
     * **Event Data**
     * - **id**: The id of the bubble that has been deleted
     */
    ON_BUBBLE_DELETED = 'ON_BUBBLE_DELETED',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a there's an update on the role of the connected user role (owner, organizer, user)
     * **Event Data**
     * - **bubble**: The related bubble
     */
    ON_CONNECTED_USER_ROLE_UPDATED = 'ON_CONNECTED_USER_ROLE_UPDATED',

    /**
     * @eventProperty
     * This RBEvent is sent when the connected user has received an invitation to join a bubble.
     * **Event Data**
     * - **bubble**: The bubble concerned by this event
     */
    ON_USER_INVITATION_RECEIVED = 'ON_USER_INVITATION_RECEIVED',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a user has accepted the invitation in the bubble
     * **Event Data**
     * - **bubble**: The bubble concerned by this event
     * - **bubbleUser**: The bubble user who has accepted the invitation
    */
    ON_USER_INVITATION_ACCEPTED = 'ON_USER_INVITATION_ACCEPTED',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a user has declined the invitation to join the bubble
     * **Event Data**
     * - **bubble**: The bubble concerned by this event
     * - **bubbleUser**: The bubble user who has declined the invitation
     */
    ON_USER_INVITATION_DECLINED = 'ON_USER_INVITATION_DECLINED',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a user has left the bubble of his own
     * **Event Data**
     * - **bubble**: The bubble concerned by this event
     * - **bubbleUser**: The bubble user who has left the bubble
     */
    ON_USER_BUBBLE_LEFT = 'ON_USER_BUBBLE_LEFT',

    /**
     * @eventProperty
     * This `RBEvent` is sent in the following cases:
     *  - if a user has been removed from the bubble by the owner or one of the organizers.
     *  - if the bubble in which the user was a member has been destroyed.
     * **Event Data**
     * - **bubble**: The bubble concerned by this event
     * - **bubbleUser**: The bubble user who has been removed from the bubble
     */
    ON_USER_BUBBLE_REMOVED = 'ON_USER_BUBBLE_REMOVED',

    /**
     * @internal
     * @eventProperty
     * This RBEvent is send when there's a request to join a bubble has been receveid
     * **Event Data**
     * - **bubble**: The bubble concerned by this event
     * - **lobbyUser**: The newly added lobby user
     */
    ON_REQUEST_TO_JOIN_BUBBLE_RECEIVED = 'ON_REQUEST_TO_JOIN_BUBBLE_RECEIVED',

    /**
     * @internal
     * @eventProperty
     * This RBEvent is send when there's a change in any bubble lobby list users. There's event coming inside the bubble (in case of bubble.subscribe) but this event
     * helps to manage a global counter and vision on all bubbles lobby list / waiting list
     */
    ON_BUBBLES_LOBBY_LIST_CHANGE = 'ON_BUBBLES_LOBBY_LIST_CHANGE',

    /**
     * @internal
     * @eventProperty
     * This RBEvent is send when there's a change in the current user's request to join bubbles. This event can also be received when the user ACK Read or removes the request.
     * This event helps to manage a global counter and vision on all requests to join bubbles
     */
    ON_MY_USER_REQUESTS_TO_JOIN_BUBBLE_CHANGE = 'ON_MY_USER_REQUESTS_TO_JOIN_BUBBLE_CHANGE'
}

/**
 * @internal
 * Conversation service internal events send to other sevices via sendInnerEvent()
 * Should not be used by SDK user
 */
export enum BubbleServiceInnerEvents {
    /**
     * This RB event is send when a "admin" message is added in the bubble (for hunting group voicemails etc)
     * @param roomJid - the room JID
     * @param userJid - the user JID
     * @param eventName - the name of the event
     * @param msgId - the message ID
     * @param extraParameters - object with extra params
     */
    BUBBLE_ADMIN_MESSAGE_EVENT = 'BUBBLE_ADMIN_MESSAGE_EVENT',

    /**
     * This event is send when the bubble history is now fully ready (on accept of new bubble for instance)
     * @param bubble - the related bubble
     */
    BUBBLE_HISTORY_READY = 'BUBBLE_HISTORY_READY'
}

/**
 * The `BubbleCreationData` interface describes the options that can be sent to the createBubble() method
 * @example
 * ```ts
 * const data = {
 *      "description": "my bubble description",
 *      "history": true,
 *      "autoAcceptInvitation": true
 * }
 * createBubble("my bubble name", data);
 * ```
 */
export interface BubbleCreationData {

    /**
     * The bubble description
     */
    description?: string;

    /**
     * Specifies whether the previous messages history are shown or not to a new bubble user.
     * By default, this parameter is 'true'.
     */
    history?: boolean;

    /**
     * Avatar image for the bubble
     */
    bubbleAvatar?: any;

    /**
     * When set to true, allows to automatically add participants in the bubble (default behavior is that participants need to accept the bubble invitation first before being a member of this bubble)
     * `Default : FALSE`
     */
    autoAcceptInvitation?: boolean;

    /**
     * In case of conference in this bubble, all participants will be muted upon join
     * `Default : FALSE`
     */
    muteUponEntry?: boolean;

    /**
     * In the event of a conference in this bubble, specify whether a local tone should be played each time a participant joins the conference.
     * By default, this parameter is 'false'.
     */
    playEntryTone?: boolean;

    /**
     * List of contacts that we want to invite to the bubble
     */
    contacts?: Contact[];

    /**
     * List of emails that we want to invite to the bubble (will receive an invitation email)
     */
    guestEmails?: string[];

    /**
     * Add optional body parameter role ('user', 'organizer'). By default, all users get the simple 'user' role.
     * Using 'role' parameter all invited users may have the same role in this bulk.
     */
    role?: BubbleUserRole;

    /**
     * The visibility of the bubble
     * A public bubble can be found via a search operation (using 'searchBubbles()' method) or get by its identifier
     * even if a user is not a member of this bubble. Information on such bubbles are provided for guidance only.
     * A private bubble can be joined only by invitation or via a public link, if the bubble has been shared (bubble configuration parameter).
     * By default, the visibility is 'private'.
     */
    visibility?: BubbleVisibility;
}

/**
 * List of different ways to search for a bubble
 */
export enum BubbleSearchScope {
    /**
     * The bubble search must be performed on the name of the bubble
     */
    SEARCH_BY_BUBBLE_NAME = "SEARCH_BY_BUBBLE_NAME",

    /**
     * The bubble search must be performed on the name of the tag(s) set for the bubble
     */
    SEARCH_BY_TAG_NAME = "SEARCH_BY_TAG_NAME",

    /**
     * The bubble search must be performed oin the bubble's participant(s) names.
     */
    SEARCH_BY_PARTICIPANT_NAME = "SEARCH_BY_PARTICIPANT_NAME",
}

export enum BubbleSearchSortBy {
    /**
     * The list of bubbles found must be returned sorted by bubble name.
     */
    SORT_BY_BUBBLE_NAME = "SORT_BY_BUBBLE_NAME",

    /**
     * The list of bubbles found must be returned sorted by date of last activity.
     */
    SORT_BY_LAST_ACTIVITY = "SORT_BY_LAST_ACTIVITY",
}

export enum BubbleSearchFoundBy {
    /**
     * Specifies that a bubble was found via a pattern matching on its name
     */
    FOUND_BY_BUBBLE_NAME = "FOUND_BY_BUBBLE_NAME",

    /**
     * Specifies that a bubble was found via a pattern matching on the name of some tag(s) set for the bubble

     */
    FOUND_BY_TAG_NAME = "FOUND_BY_TAG_NAME",

    /**
     * Specifies that a bubble was found via a pattern matching on the bubble's participant(s) names.
     */
    FOUND_BY_PARTICIPANT_NAME = "FOUND_BY_PARTICIPANT_NAME",
}

export interface BubbleSearchOptions {
    /**
     * Options to specify the scope of the search, i.e. the 'name' properties to be used as scope for the search
     * The name properties that can be specified are:
     * - The name of the bubble
     * - The name of tag(s) set for the bubble
     * - The name of bubble participants.
     * One or more name properties can be optionally specified to define the scope of the search.
     * If no scope is specified, the search is performed by default on the name of the bubble only.
     */
    scope?: BubbleSearchScope[];

    /**
     * Options for specifying how the list of bubbles returned as results should be sorted.
     */
    sortBy?: BubbleSearchSortBy;
}

export interface BubbleSearchResult {
    /**
     * The bubble found matching the search criteria.
     */
    bubble: Bubble;

    /**
     * Specifies how the bubble was found, i.e. on which name properties the pattern matched.
     */
    foundBy: BubbleSearchFoundBy[];
}

/**
 * The `BubbleService` interface defines the Rainbow SDK BubbleService API
 */
export interface BubbleService {
    /**
     * Indicates whether the Bubble Service has been started or not.
     * If this is not the case, no bubble can yet been retrieved.
     */
    started: boolean;

    /**
     * Subscribe to the invitations updates; All events send to the callback are of type `RBEvent`
     * @param callback - The callback function that will be called when events are received
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) A list of event names that the user wants to receive when subscribing (used as filter)

     */
    subscribe(callback: (event: RBEvent<BubbleServiceEvents>) => any, eventNames?: BubbleServiceEvents | BubbleServiceEvents[]): Subscription;

    /**
     * Create a new bubble; This API also sends BubbleServiceEvents.ON_BUBBLE_CREATED event
     * Returns a Promise with the new created bubble.
     * @param bubbleName - The name of the bubble
     * @param createBubbleData - Bubble settings that can be specified when it is created.
     */
    createBubble(bubbleName: string, createBubbleData?: BubbleCreationData): Promise<Bubble>;

    /**
     * Get a bubble from its identifier.
     * @param bubbleId - The bubble dbId or bubble JID
     */
    getBubble(bubbleId: string): Promise<Bubble | undefined>;

    /**
     * Get all bubbles, restricted to one type if specified.
     * The 'type' parameter allows to filter the type of bubbles to be returned:
     * - 'IN_USE':
     *   Returns the active bubbles of which the connected user is a member, whether as a user or as an organizer.
     *   Note that a bubble is said to be ‘active’ if it has not been archived or deleted.
     * - 'MINE':
     *   Returns the bubbles whose the connected user is the owner.
     *   This includes bubbles that the user has created themselves as well as bubbles for which the user has benefited from the transfer of ownership.
     * - 'ARCHIVED':
     *   Returns the bubbles archived by the user.
     *   An archived bubble is a bubble that the connected user has left but has decided not to delete but to keep.
     *   By creating an archive, the user will no longer be able to publish information, but will retain a history of the conversations of all participants.
     *   - 'WITH_CONFERENCE':
     *   Returns the bubbles with active conference inside
     *   This list will contain all bubbles that current have an active conference, including the one that the user may currently participate
     *
     * If the 'type' parameter is not specified, this method returns all the bubbles "currently loaded".
     * This means the bubbles that are loaded by default when the service is started (those with an active conversation) and
     * those that are subsequently requested via the ‘type’ parameter.
     *
     * **Please note:** this method is asynchronous, as the time taken to retrieve bubbles may vary depending on whether or not they have already been requested.
     * @param type - The type of bubbles required.
     */
    getBubbles(type?: 'IN_USE' | 'MINE' | 'ARCHIVED' | 'WITH_CONFERENCE'): Promise<Bubble[]>;

    /**
     * Get all invitations for which the connected user is invited to join a bubble.
     */
    getBubbleInvitations(): BubbleInvitation[];

    /**
     * This API allows to search Rainbow bubbles that match a search criteria applied on some name properties such as the name of the bubble
     * or the names of bubble participants for example.
     * One or more name properties can be optionally specified to define the scope of the search.
     * If no scope is specified, the search is performed by default on the name of the bubble only.
     * It is also possible to optionally specify how the list of bubbles returned as results should be sorted.
     * By default, the bubbles list will be sorted by bubble name.
     * @param searchPattern - The search criteria
     * @param options - Options to specify the scope of the search and how the results are to be returned.
     */
    searchBubbles(searchPattern: string, options?: BubbleSearchOptions): Promise<BubbleSearchResult[]>;

    /**
     * Method to find bubbles by tag
     * Takes into account tags separated by space
     * @param tagNames - tag names to find
     * @param exactMatch - whether we find exact matching tags
     */
    searchBubblesByTag(tagNames: string, exactMatch?: boolean): Promise<Bubble[]>;

    /**
     * This API allows to evaluate the WEBRTC connectivity of the user with the conference in the bubble
     * The results of the test may help troubleshoot connectivity issues and evaluate the quality of the connection to Rainbow infrastructure.
     * @param testLength - length during wchice the timeid manually set. Drfault to 5.
     *
     */
    startMediaEchoTest(testLength: number): Promise<void>;
}

/**
 * @internal
 * Configuration information used for tweak bubble service start
 */
export class BubbleServiceConfig {
    ignoreOpenInviteData?: any;
    webinar?: boolean = false;
    ignoreRole?: boolean = false;
}

/**
 * @internal
 */
export class BubbleServiceRB extends Service implements BubbleService {

    public openInviteIdByRoomIdSubject: Subject<any> = new Subject<any>();
    public starting: boolean = false;
    public started: boolean = false;
    public context: string = 'global';

    private bubbles: Record<string, BubbleRB> = {};
    private rxSubject: Subject<RBEvent<BubbleServiceEvents>> = new Subject<RBEvent<BubbleServiceEvents>>();
    private innerRxSubject: Subject<RBEvent<BubbleServiceInnerEvents>> = new Subject<RBEvent<BubbleServiceInnerEvents>>();
    private preload: any = { accepted: false, invited: false, unsubscribed: false };
    private portalURL?: string = undefined;
    private roomPortalURL?: string = undefined;
    private listeners = [];
    private xmppConnectionSubscription = null;
    private roomPresenceWaitingList = [];
    private userContact?: Contact;
    private bubblesByJids: Record<string, BubbleRB> = {};
    private bubblesInProgress: Record<string, Promise<BubbleRB>> = {};
    private maxBubblesUsers: number = 0;
    private maxBubblesStartupLoad: number = 0;
    private openInviteIdByRoomId: any = {};
    private getBubbleByJidPromises: Record<string, Promise<BubbleRB>> = {};
    private createBubbleInProgress: Promise<BubbleRB>;
    private bubblePresenceRef?: StropheHandler;
    private bubbleInfoMessageRef?: StropheHandler;
    private bubbleMessageConfigRef?: StropheHandler;
    private bubbleHistoryInfoRef?: StropheHandler;
    private bubbleNotificationRef?: StropheHandler;
    private bubbleErrorMessageRef?: StropheHandler;
    private bubbleServiceConfig?: BubbleServiceConfig = null;
    private loadAllAcceptedBubblesPromise: Promise<void> = null;

    //object containing array of LobbyUser for each entry
    //key is bubble DB ID, value is Contact[]
    private lobbyUsersPerBubble: Record<string, LobbyUser[]> = {};

    //key is bubble DB ID where the connected user is in the waiting lobby, value is the addition date (when the ask was made)
    private connectedUserPendingRequests: Record<string, { status: AccessRequestStatus; additionDate: string; bubbleName?: string; ackRead?: boolean; }> = {};

    private promiseQueue: PromiseQueue = PromiseQueue.create();

    private settingsService: SettingsService = SettingsService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private i18n: I18nService = I18nService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private fileStorageService?: FileStorageService;
    private webConferenceService?: BubbleConferenceServiceRB;
    private bubbleTagService?: BubbleTagService;

    // ------------------------------------------------------------
    //                   LIFE CYCLE STUFF
    // ------------------------------------------------------------

    public static getInstance() { return Service.create(BUBBLE_SVC, BubbleServiceRB); }

    public static build() { return new BubbleServiceRB(); }

    private constructor() { super(); }

    public async start(bubbleServiceConfig?: BubbleServiceConfig) {
        try {
            this.bubbleServiceConfig = bubbleServiceConfig;
            this.userContact = this.contactService.userContact;
            this.starting = true;
            this.portalURL = config.restServerUrl + '/api/rainbow/enduser/v1.0/';
            this.roomPortalURL = config.restServerUrl + '/api/rainbow/room/v1.0/enduser/';
            this.bubbles = {};
            this.bubblesByJids = {};
            this.bubblesInProgress = {};
            this.openInviteIdByRoomId = {};
            this.getBubbleByJidPromises = {};

            this.maxBubblesUsers = this.profileService.getFeatureLimitMax(this.profileService.FeaturesEnum.BUBBLE_PARTICIPANT_COUNT);
            this.maxBubblesStartupLoad = this.profileService.getFeatureLimitMax(this.profileService.FeaturesEnum.BUBBLE_MAX_LOAD_STARTUP);

            this.fileStorageService = this.mainService.inject<FileStorageService>(FILE_STORAGE_SVC);
            this.webConferenceService = this.mainService.inject<BubbleConferenceServiceRB>(BUBBLE_CONFERENCE_SVC);
            this.bubbleTagService = this.mainService.inject<BubbleTagService>(BUBBLE_TAG_SVC);

            if (this.fileStorageService) {
                this.logger.infoBlue('[bubbleService] === FILE SERVICE PLUGIN LOADED, ROOM SERVICE CAN WORK WITH FILES ===');
            }

            this.context = this.userContact?.roomsEnabled ? 'global' : 'webinar';

            this.logger.info('');
            this.logger.info('[bubbleService] === STARTING ===');

            this.attachHandlers();
            await this.sendInitialBulkRoomPresence();
            await this.getInitServerRooms();

            if (this.context === 'global') {
                if (!this.bubbleServiceConfig?.ignoreOpenInviteData) { this.retrieveOpenInviteData(); }
            }
            this.logger.info('[bubbleService] === STARTED ===');
            this.started = true;
            this.sendEvent(BubbleServiceEvents.ON_SERVICE_STARTED);
        }
        catch (error) {
            this.logger.error('[bubbleService] === STARTING FAILURE === ' + error.message);
            this.reconnect();
        }
    }

    public async stop() {

        this.logger.info('');
        this.logger.info('[bubbleService] === STOPPING ===');

        // Destroy listeners
        this.listeners.forEach((listener) => { listener(); });

        // Clear rooms cache
        this.bubbles = {};
        this.bubblesByJids = {};
        this.roomPresenceWaitingList = [];
        this.starting = this.started = false;
        this.loadAllAcceptedBubblesPromise = null;
        this.createBubbleInProgress = null;
        this.preload = { accepted: false, invited: false, unsubscribed: false };
        this.logger.info('[bubbleService] === STOPPED ===');
    }

    public attachHandlers() {
        this.logger.info('[bubbleService] attachHandlers');
        if (this.bubblePresenceRef) { this.xmppService.connection.deleteHandler(this.bubblePresenceRef); this.bubblePresenceRef = undefined; }
        this.bubblePresenceRef = this.xmppService.connection.addHandler((stanza) => { return this.onBubblePresence(stanza); }, 'http://jabber.org/protocol/muc#user', 'presence', null);
        if (this.bubbleInfoMessageRef) { this.xmppService.connection.deleteHandler(this.bubbleInfoMessageRef); this.bubbleInfoMessageRef = undefined; }
        this.bubbleInfoMessageRef = this.xmppService.connection.addHandler((stanza) => { this.onBubbleEventMessage(stanza); return true; }, null, 'message', 'groupchat');
        if (this.bubbleMessageConfigRef) { this.xmppService.connection.deleteHandler(this.bubbleMessageConfigRef); this.bubbleMessageConfigRef = undefined; }
        this.bubbleMessageConfigRef = this.xmppService.connection.addHandler((stanza) => { this.promiseQueue.add(() => this.onBubbleUpdateConfigMessage(stanza)); return true; }, null, 'message', 'management');
        if (this.bubbleHistoryInfoRef) { this.xmppService.connection.deleteHandler(this.bubbleHistoryInfoRef); this.bubbleHistoryInfoRef = undefined; }
        this.bubbleHistoryInfoRef = this.xmppService.connection.addHandler((stanza) => { return this.onBubbleHistoryReadyMessage(stanza); }, 'jabber:iq:notification', 'message', null);
        if (this.bubbleNotificationRef) { this.xmppService.connection.deleteHandler(this.bubbleNotificationRef); this.bubbleNotificationRef = undefined; }
        this.bubbleNotificationRef = this.xmppService.connection.addHandler((stanza) => { this.onBubbleInvitationMessage(stanza); return true; }, 'jabber:x:bubble:conference', 'message', null);
        if (this.bubbleErrorMessageRef) { this.xmppService.connection.deleteHandler(this.bubbleErrorMessageRef); this.bubbleErrorMessageRef = undefined; }
        this.bubbleErrorMessageRef = this.xmppService.connection.addHandler((stanza) => { return this.onBubbleErrorMessage(stanza); }, null, 'message', 'error');

        // Destroy listeners
        this.listeners.forEach((listener) => { listener(); });
        if (this.xmppConnectionSubscription) { this.xmppConnectionSubscription.unsubscribe(); }
        this.xmppConnectionSubscription = this.xmppService.subscribeToConnectionEvents((event) => { this.onConnectionStateChange(event); });
    }

    public async reconnect(attempt: number = 0) {
        try {
            this.logger.info('[bubbleService] reconnect');
            attempt++;
            this.attachHandlers();

            // Reset all rooms before reconnection
            Object.keys(this.bubbles).forEach((key: string) => {
                const room = this.bubbles[key];
                if (room) {
                    room.ready = false;
                    room.usersReady.next(false);
                    if (room.initPresInterval) {
                        this.logger.info('[bubbleService] onConnectionStateChange cancel initPresence retryer for room: ' + room.jid + ' ' + room.getNameForLogs());
                        room.initPresInterval.unsubscribe();
                        room.initPresInterval = null;
                    }
                    if (room.initPresPromise) {
                        this.logger.info('[bubbleService] onConnectionStateChange remove initPresPromise for room: ' + room.jid + ' ' + room.getNameForLogs());
                        room.initPresPromise = null;
                        room.initPresPromiseResolve = null;
                    }
                }
            });
            this.roomPresenceWaitingList = [];

            this.preload['invited'] = this.preload['accepted'] = false;
            this.loadAllAcceptedBubblesPromise = null;
            await this.sendInitialBulkRoomPresence();
            await this.getInitServerRooms();

            if (this.context === 'global') {
                if (!this.bubbleServiceConfig?.ignoreOpenInviteData) { this.retrieveOpenInviteData(); }
            }
            this.logger.info('[bubbleService] reconnect success');

            this.sendEvent(BubbleServiceEvents.ON_SERVICE_STARTED);
        }
        catch (error) {
            this.logger.error('[bubbleService] reconnect failure -- ' + error.message);
            if (attempt === 1) { this.reconnect(attempt); }
        }
    }

    private onConnectionStateChange(event: any) {
        this.logger.info('[bubbleService] onConnectionStateChange status:' + event.name);
        if (event.name === 'ON_XMPP_DISCONNECTED' && this.bubbles) {
            Object.keys(this.bubbles).forEach((key) => {
                const room = this.bubbles[key];
                if (room) {
                    room.ready = false;
                    if (room.initPresInterval) {
                        this.logger.info('[bubbleService] onConnectionStateChange cancel initPresence retryer for room: ' + room.jid + ' ' + room.getNameForLogs());
                        room.initPresInterval.unsubscribe();
                        room.initPresInterval = null;
                    }
                    if (room.initPresPromise) {
                        this.logger.info('[bubbleService] onConnectionStateChange remove initPresPromise for room: ' + room.jid + ' ' + room.getNameForLogs());
                        room.initPresPromise = null;
                        room.initPresPromiseResolve = null;
                    }
                }
            });
            this.roomPresenceWaitingList = [];
        }
    }

    private async getInitServerRooms(): Promise<void> {
        this.logger.info(`[bubbleService] getInitServerRooms`);
        const startDate = performance.now();
        const roomsData = await Promise.all([
            this.getInitServerRoomsByType('invitation'),
            this.getInitServerRoomsByType('conversation')
        ]);
        const concatRoomsData = [...roomsData[0], ...roomsData[1]];

        // Get minimal contact subset
        await this.getMinimalBubbleUsers(concatRoomsData);

        //clear all rooms in invitation, as the array could be wrong
        Object.keys(this.bubbles).forEach((key: string) => {
            const bubble = this.bubbles[key];
            if (bubble.bubbleConnectedUser.status === BubbleUserStatus.INVITED && bubble.description !== 'Rainbow_OutlookCreation_InternalUseOnly' && !bubble.isWebinarBubble()) {
                delete this.bubbles[key];
            }
        });

        // Create rooms
        concatRoomsData.map((roomData: any) => { return this.createOrUpdateBubbleFromData(roomData, true, true); });

        const endDate = performance.now();
        this.logger.info(`[bubbleService] getInitServerRooms - done - get ${concatRoomsData.length} rooms in ${Math.round(endDate - startDate)} ms`);

        if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.BUBBLE_WAITING_ROOM)) {
            //get the lobby / waiting room list
            this.getLobbyListsFromServer();
        }
    }

    private async getInitServerRoomsByType(type: string): Promise<any[]> {
        try {
            // Get rooms data
            const startDate = performance.now();
            let url = `${this.portalURL}rooms?format=full&needIsAlertNotificationEnabled=false&userId=${this.userContact.dbId}&offset=0&nbUsersToKeep=3`;
            switch (type) {
                case 'invitation': url += '&limit=1000&status=invited&webinar=true'; break;
                case 'conversation': url += '&limit=20&webinar=false&status=accepted&sortField=lastActivityDate'; break;
                case 'webinar': url += '&limit=1000&status=accepted&webinar=true'; break;
                default: break;
            }

            if (this.context === 'webinar') url += '&context=webinar';
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData = await response.json();
            const endDate = performance.now();
            const nbRooms = responseData.data ? responseData.data.length : 0;
            this.logger.info(`[bubbleService] getInitServerRoomsByType(${type}) - done - ${nbRooms} rooms fetched -- ${Math.round(endDate - startDate)} ms`);
            return responseData.data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `getInitServerRoomsByType(${type})`);
            throw errorInfo.error;
        }
    }

    // ------------------------------------------------------------
    //                      OBSERVABLE STUFF
    // ------------------------------------------------------------

    /**
     *  Subscribe to updates from the service (All events are of RBEvent type {name: string, data: any});
     *  NB There might be some events that are not documented here, they're send on updates of different properties of the rooms.
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - array of event to listen
     */
    public subscribe(handler: (event: RBEvent<BubbleServiceEvents>) => any, eventNames?: BubbleServiceEvents | BubbleServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<BubbleServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<BubbleServiceEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[ContactService] subscribe () -- error -- ${error.message}`); }
        });
    }

    /**
     * Bubble service events send to other services via sendEvent()
     * Can listen to it via bubbleService.subscribe() API
     * @param name - the name of the event
     * @param data - the data to be send (an object
     */
    public sendEvent(name: BubbleServiceEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create<BubbleServiceEvents, any>(name, data));
    }

    public subscribeToInnerEvents(handler: (event: RBEvent<BubbleServiceInnerEvents>) => any): Subscription {
        return this.innerRxSubject.subscribe(handler);
    }

    public sendInnerEvent(name: BubbleServiceInnerEvents, data: any = null) {
        this.innerRxSubject.next(RBEvent.create<BubbleServiceInnerEvents, any>(name, data));
    }

    // ------------------------------------------------------------
    //                      PUBLIC ACCESSORS
    // ------------------------------------------------------------

    /**
     * Load all room from server in a given status
     * @param status - (values: 'invited', 'accepted','unsubscribed')
     */
    public async getAllServerBubbles(status: string = 'accepted'): Promise<void> {
        if (!this.preload[status]) {
            const startDate = performance.now();
            const limit = this.maxBubblesStartupLoad ? this.maxBubblesStartupLoad : 1000;
            this.logger.info(`[bubbleService] getAllServerBubbles - ${status} - ${limit} - init`);
            let offset = 0;
            const bubblesDataPage = await this.getServerBubblesDataPage(status, offset, limit);
            offset += limit;
            while (offset < bubblesDataPage.total) {
                await this.getServerBubblesDataPage(status, offset, limit);
                offset += limit;
            }
            const endDate = performance.now();
            this.logger.info(`[bubbleService] getAllServerBubbles - ${status} - done - ${Math.round(endDate - startDate)} ms`);
        }
        this.preload[status] = true;
    }

    /**
     *
     */
    private async getLobbyListsFromServer() {
        try {
            this.logger.info(`[bubbleService] getLobbyListsFromServer - init`);
            const promisesList = [];
            promisesList.push(this.getMyLobbyListsAsModerator());
            promisesList.push(this.getMyLobbyListsAsUser());
            promisesList.push(this.getMyAccessRequestListStatus());

            await allSettled(promisesList);

            this.logger.info(`[bubbleService] getLobbyListsFromServer - success`);
            this.sendEvent(BubbleServiceEvents.ON_BUBBLES_LOBBY_LIST_CHANGE);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `getLobbyListsFromServer`);
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            //do not throw error as this API is not blocking for starting of bubbles
        }
    }

    private async getMyLobbyListsAsUser() {
        try {
            this.logger.info(`[bubbleService] getMyLobbyListsAsUser - init`);
            const url = `${this.portalURL}rooms/lobbies`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData = await response.json();

            this.logger.info(`[bubbleService] getMyLobbyListsAsUser - success`);
            const data = responseData.data;

            const promisesList = [];

            data.forEach((element) => {
                if (element) {
                    const bubbleInfos = { dbId: element.id, additionDate: element.additionDate };
                    promisesList.push(this.addConnectedUserToWaitingListForBubble(bubbleInfos, AccessRequestStatus.PENDING, element.name));
                }
            });

            await allSettled(promisesList);

            this.sendEvent(BubbleServiceEvents.ON_MY_USER_REQUESTS_TO_JOIN_BUBBLE_CHANGE);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `getMyLobbyListsAsModerator`);
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            //do not throw error as this API is not blocking for starting of bubbles
        }
    }

    private async getMyLobbyListsAsModerator() {
        try {
            this.logger.info(`[bubbleService] getMyLobbyListsAsModerator - init`);
            const url = `${this.portalURL}rooms/lobbies/managed`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData = await response.json();

            this.logger.info(`[bubbleService] getMyLobbyListsAsModerator - success`);
            const data = responseData.data;

            const promisesList = [];

            data.forEach((element) => {
                if (element?.lobby?.length) {
                    const contactsInfos = element?.lobby?.flatMap((data) => {
                        if (data?.id)
                            return { dbId: data.id, additionDate: data.additionDate };
                        return [];
                    });
                    if (contactsInfos?.length) { promisesList.push(this.addUsersToLobbyForBubble(element.room.id, contactsInfos)); }
                }
            });

            await allSettled(promisesList);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `getMyLobbyListsAsModerator`);
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            //do not throw error as this API is not blocking for starting of bubbles
        }
    }

    private async getMyAccessRequestListStatus() {
        try {
            this.logger.info(`[bubbleService] getMyAccessRequestListStatus - init`);
            const url = `${this.portalURL}rooms/lobbies/status`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData = await response.json();

            this.logger.info(`[bubbleService] getMyAccessRequestListStatus - success`);
            const data = responseData.data;

            const promisesList = [];

            data.forEach((element) => {
                if (element) {
                    const bubbleInfos = { dbId: element.id, additionDate: element.lastStatusUpdateDate };
                    const status = (element.roomAccessRequestStatus === "pending") ? AccessRequestStatus.PENDING : ((element.roomAccessRequestStatus === "accepted") ? AccessRequestStatus.ACCEPTED : AccessRequestStatus.DENIED);
                    const hideRoomAccessRequest = element.hideRoomAccessRequest;
                    //we get pending requests from the other API, no need to add them twice
                    if (status !== AccessRequestStatus.PENDING && !hideRoomAccessRequest) {
                        promisesList.push(this.addConnectedUserToWaitingListForBubble(bubbleInfos, status, element.name, element.lastStatusUpdateAck));
                    }
                }
            });

            await allSettled(promisesList);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `getMyAccessRequestListStatus`);
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            //do not throw error as this API is not blocking for starting of bubbles
        }
    }

    /**
     * API not used in UCAAS, but could be useful for CPAAS once we add lobbys for SDK
     * @param bubble - the bubble where we want to get the list of pending users
     */
    public async getBubbleLobbyUsersList(bubble: BubbleRB) {
        try {
            if (!bubble) { throw new Error("[bubbleService] getBubbleLobbyUsersList -- bubble not found"); }

            this.logger.info(`[bubbleService] getBubbleLobbyUsersList for bubble ${bubble.dbId}`);

            const url = `${this.portalURL}rooms/${bubble.dbId}/lobbies/pending`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData = await response.json();

            this.logger.info(`[bubbleService] getBubbleLobbyUsersList for bubble ${bubble.dbId} -- sucess`);
            const contactsInfos = responseData.data?.lobby?.map((data) => {
                return { dbId: data.id, additionDate: data.additionDate };
            });

            if (contactsInfos?.length) { this.addUsersToLobbyForBubble(bubble.dbId, contactsInfos); }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `getBubbleLobbyList`);
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public getUsersInLobbyForBubble(bubble: Bubble): LobbyUser[] {
        return this.lobbyUsersPerBubble[bubble?.dbId] ? this.lobbyUsersPerBubble[bubble?.dbId] : [];
    }

    /**
     * Return an object, containing a key - value list, where key is the bubble ID
     */
    public async getAllBubblesWithUsersInLobby(): Promise<Bubble[]> {
        const bubbles = [];

        for (const [key, value] of Object.entries(this.lobbyUsersPerBubble)) {
            if (value?.length) {
                try {
                    const bubble = await this.getBubble(key);
                    bubbles.push(bubble);
                }
                catch { this.logger.error(`[bubbleService] getAllBubblesWithUsersInLobby -- error on bubble ${key}`); }
            }
        }

        return bubbles;
    }

    /**
     * This API allows the user to "mark as read" an access request and/or remove it from the list
     * @param requestId - the request ID
     * @param ackRead - if the request should be marked as READ, (allows the user to manage a notification center related to updates on his pending request) An access request with the status pending, accepted or rejected can be acknowledged. These acknowledgment can't be undo
     * @param removeFromList - remove the access request from the list. Only an access request with the status accepted or rejected can be removed
     */
    public async updateUserRequestToJoinBubble(requestId: string, ackRead: boolean = false, removeFromList: boolean = false) {
        try {
            // Get rooms data
            if (!requestId || !this.connectedUserPendingRequests[requestId]) { throw new Error("[bubbleService] updateUserRequestToJoinBubble -- request not found"); }

            const data = {
                ack: ackRead,
                hide: removeFromList
            };

            this.logger.info(`[bubbleService] updateUserRequestToJoinBubble - for bubble ${requestId} ackRead ${ackRead} and remove from list ${removeFromList}`);
            const url = `${this.portalURL}rooms/${requestId}/lobbies/users`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(data);
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }

            if (ackRead && this.connectedUserPendingRequests[requestId]) {
                this.connectedUserPendingRequests[requestId].ackRead = true;
            }

            if (removeFromList) {
                delete this.connectedUserPendingRequests[requestId];
            }

            this.logger.info(`[bubbleService] updateUserRequestToJoinBubble - for bubble ${requestId} -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `updateUserRequestToJoinBubble`);
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async denyUsersWaitingInBubbleLobby(bubble: Bubble, lobbyContacts: LobbyUser[] = []) {
        try {
            // Get rooms data
            if (!bubble) { throw new Error("[bubbleService] denyUsersWaitingInBubbleLobby -- bubble not found"); }

            const data = {
                scope: 'all',
                users: []
            };
            if (lobbyContacts?.length) {
                lobbyContacts.forEach((lobbyContact) => { data.users.push(lobbyContact.contact.dbId); });
                data.scope = 'some';
            }

            this.logger.info(`[bubbleService] denyUsersWaitingInBubbleLobby - for bubble ${bubble.dbId} scope ${data.scope} and users ${data.users}`);

            const url = `${this.portalURL}rooms/${bubble.dbId}/lobbies/deny`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(data);
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }

            this.logger.info(`[bubbleService] denyUsersWaitingInBubbleLobby - for bubble ${bubble.dbId} -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `denyUsersWaitingInBubbleLobby`);
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async acceptUsersWaitingInBubbleLobby(bubble: Bubble, lobbyContacts: LobbyUser[] = []) {
        try {
            // Get rooms data
            if (!bubble) { throw new Error("[bubbleService] acceptUsersWaitingInBubbleLobby -- bubble not found"); }

            const data = {
                scope: 'all',
                users: []
            };
            if (lobbyContacts?.length) {
                lobbyContacts.forEach((lobbyContact) => { data.users.push(lobbyContact?.contact.dbId); });
                data.scope = 'some';
            }

            this.logger.info(`[bubbleService] acceptUsersWaitingInBubbleLobby - for bubble ${bubble.dbId} scope ${data.scope} and users ${data.users}`);

            const url = `${this.portalURL}rooms/${bubble.dbId}/lobbies/accept`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(data);
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }

            this.logger.info(`[bubbleService] acceptUsersWaitingInBubbleLobby - for bubble ${bubble.dbId} -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `acceptUsersWaitingInBubbleLobby`);
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Add current user to the waiting lobby for a given bubble
     * @param bubbleInfos - Bubbles infos that contain the current bubble DB ID, the status and the addition date
     */
    private async addConnectedUserToWaitingListForBubble(bubbleInfos: Record<string, string>, status: AccessRequestStatus = AccessRequestStatus.PENDING, bubbleName?: string, ackRead: boolean = false) {
        //filter users already inside
        this.connectedUserPendingRequests[bubbleInfos.dbId] = { status: status, additionDate: bubbleInfos.additionDate, bubbleName, ackRead };

        //see if the bubble is already in cache, then send event
        //if the bubble is not in cache, then do not send any event
        const bubble = this.getBubbleFromCache(bubbleInfos.dbId) as BubbleRB;
        if (bubble) { bubble.sendEvent(BubbleEvents.ON_MY_USER_IN_LOBBY_UPDATE, { status: this.connectedUserPendingRequests[bubbleInfos.dbId].status }); }
    }

    public async getMyRequestListToJoinBubbles(): Promise<{ bubble: BubbleInvitation; requestId: string; status: string; additionDate: string; bubbleName?: string; ackRead?: boolean; }[]> {
        const result = [];
        for (const [key, value] of Object.entries(this.connectedUserPendingRequests)) {
            if (value) {
                try {
                    let bubble;
                    if (value.status !== AccessRequestStatus.DENIED) { bubble = await this.getBubble(key); }
                    result.push({ bubble, requestId: key, status: value.status, additionDate: value.additionDate, bubbleName: value.bubbleName, ackRead: value.ackRead });
                }
                catch { this.logger.error(`[bubbleService] getAllBubblesWithUsersInLobby -- error on bubble ${key}`); }
            }
        }

        return result;
    }

    /**
     * Add users to the lobby for a given bubble
     * @param bubbleDbId - bubble DB ID
     * @param contactsInfos - Contact infos that are needed to construct the lobby user
     * Return an array of the NEWLY added lobby users, and not the whole list !
     */
    private async addUsersToLobbyForBubble(bubbleDbId: string, contactsInfos: Record<string, string>[]): Promise<LobbyUser[]> {
        if (!this.lobbyUsersPerBubble[bubbleDbId]) { this.lobbyUsersPerBubble[bubbleDbId] = []; }

        const result = [];

        //filter users already inside
        contactsInfos = contactsInfos.filter((info) => {
            return !this.lobbyUsersPerBubble[bubbleDbId]?.some((lobbyContact) => { return lobbyContact?.contact?.dbId === info.dbId; });
        });

        //get all contats from the server
        await allSettled(contactsInfos.map((info: any) => { return this.contactService.getContactByDBId(info.dbId); }));

        contactsInfos.map((info) => {
            const contact = this.contactService.getContactFromCacheById(info.dbId);
            if (contact) {
                this.lobbyUsersPerBubble[bubbleDbId].push({ contact, additionDate: info.additionDate });
                result.push({ contact, additionDate: info.additionDate });
            }
        });

        //see if the bubble is already in cache, then send event
        //if the bubble is not in cache, then do not send any event
        const bubble = this.getBubbleFromCache(bubbleDbId) as BubbleRB;
        if (bubble) { bubble.sendEvent(BubbleEvents.ON_USERS_IN_LOBBY_UPDATE, { lobbyUsers: this.lobbyUsersPerBubble[bubble.dbId] }); }

        return result;
    }

    /**
     * Async method which get get a bubble.
     * The bubble is first search in service cache then server side.
     * @param bubbleRef - a bubble dbId or bubble JID
     */
    public async getBubble(bubbleRef: string): Promise<Bubble | undefined> {
        try {
            let bubble = this.getBubbleFromCache(bubbleRef);
            if (!bubble) {
                // Before we get the room from server, see if we have an active create room in progress and wait for 250 ms
                if (this.createBubbleInProgress) {
                    await this.createBubbleInProgress;
                    // Restart the call of the API to see if room is now in the cache
                    return this.getBubble(bubbleRef);
                }

                if (bubbleRef.indexOf("room_") !== -1) bubble = await this.getServerBubbleByJid(bubbleRef);
                else bubble = await this.getServerBubble(bubbleRef);
            }
            return bubble;
        }
        catch (error) {
            this.logger.error(`[bubbleService] getBubble -- FAILURE -- ${error.message}`);
            return undefined;
        }
    }

    /**
     * synchronous method used to get a existing bubble from the cache
     * @param id - should be the bubble id or dbId
     */
    public getBubbleFromCache(id: string): Bubble | undefined {
        let bubble = this.bubbles[id];
        if (!bubble) bubble = this.bubblesByJids[id];
        return bubble;
    }

    /**
     * Return an array of all bubbles already in the cache
     */
    public getBubblesFromCache(): Bubble[] {
        return Object.keys(this.bubbles).map(key => this.bubbles[key]);
    }

    /**
     * Get all bubbles, restricted to one type if specified.
     * The 'type' parameter allows to filter the type of bubbles to be returned:
     * - 'IN_USE':
     *   Returns the active bubbles of which the connected user is a member, whether as a user or as an organizer.
     *   Note that a bubble is said to be ‘active’ if it has not been archived or deleted.
     * - 'MINE':
     *   Returns the bubbles whose the connected user is the owner.
     *   This includes bubbles that the user has created themselves as well as bubbles for which the user has benefited from the transfer of ownership.
     * - 'ARCHIVED':
     *   Returns the bubbles archived by the user.
     *   An archived bubble is a bubble that the connected user has left but has requested the creation of an archive for this bubble.
     *   By archive we mean that the bubble will always be accessible to the user, but in consultation mode only.
     *   The user will still be able to view the bubble information (name, subject), members and the conversation history up to the moment he leave the bubble.
     *   But he will no longer be able to communicate in the bubble (sending messages or starting a conference).
     *
     * If the 'type' parameter is not specified, this method returns all the bubbles "currently loaded".
     * This means the bubbles that are loaded by default when the service is started (those with an active conversation) and
     * those that are subsequently requested via the ‘type’ parameter.
     *
     * **Please note:** this method is asynchronous, as the time taken to retrieve bubbles may vary depending on whether or not they have already been requested.
     * @param type - The type of bubbles required.
     */
    public async getBubbles(type?: 'IN_USE' | 'MINE' | 'ARCHIVED' | 'WITH_CONFERENCE'): Promise<Bubble[]> {
        if (!type) {
            return this.getOrderedRooms().filter(bubble => (bubble.bubbleConnectedUser?.status !== BubbleUserStatus.INVITED) && !bubble.isWebinarBubble());
        }

        switch (type) {
            case 'IN_USE':
                await this.getAllServerBubbles(BubbleUserStatus.ACCEPTED);
                return this.getOrderedRooms().filter(bubble => ((bubble.bubbleConnectedUser?.status === BubbleUserStatus.ACCEPTED) && !bubble.isWebinarBubble()));

            case 'MINE':
                await this.getAllServerBubbles(BubbleUserStatus.ACCEPTED);
                return this.getOrderedRooms().filter(bubble => (bubble.isConnecteUserOwner()
                    && (bubble.bubbleConnectedUser?.status === BubbleUserStatus.ACCEPTED)
                    && !bubble.isWebinarBubble()));

            case 'ARCHIVED':
                await this.getAllServerBubbles(BubbleUserStatus.UNSUBSCRIBED);
                return this.getOrderedRooms().filter(bubble => (bubble.bubbleConnectedUser?.status === BubbleUserStatus.UNSUBSCRIBED) && !bubble.isWebinarBubble());

            case 'WITH_CONFERENCE':
                await this.getAllServerBubbles(BubbleUserStatus.ACCEPTED);
                return this.getBubblesWithActiveConference().filter(bubble => ((bubble.bubbleConnectedUser?.status === BubbleUserStatus.ACCEPTED) && !bubble.isWebinarBubble()));

            default:
                return [];
        }
    }

    /**
     * Return an array of all bubbles with an active conference at the moment
     */
    public getBubblesWithActiveConference(): Bubble[] {
        return Object.keys(this.bubbles).reduce((array: BubbleRB[], key: string) => {
            if (this.bubbles[key]?.isBubbleConferenceStarted()) array.push(this.bubbles[key]);
            return array;
        }, []);
    }

    /**
     * Return an array of all bubbles with an active conference at the moment that we've not yet joined
     */
    public getBubblesWithActiveConferenceNotJoined(): Bubble[] {
        return Object.keys(this.bubbles).reduce((array: BubbleRB[], key: string) => {
            if (this.bubbles[key]?.isBubbleConferenceStarted()) {
                const webConference = this.webConferenceService?.getWebConferenceSessionById(this.bubbles[key].dbId);
                if (!webConference?.haveJoined) {
                    array.push(this.bubbles[key]);
                }
            }
            return array;
        }, []);
    }

    /**
     * return an array of all MY rooms
     */
    public getMyBubbles(): Bubble[] { return this.getBubblesFromCache().filter((bubble: BubbleRB) => { return bubble.isConnecteUserOwner(); }); }

    public searchRooms(criteria: string): Bubble[] {
        const queries = criteria.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').split(/[ -]+/);
        return this.getBubblesFromCache().filter((room: BubbleRB) => {
            if (room.bubbleConnectedUser.status !== BubbleUserStatus.ACCEPTED || room.isWebinarBubble()) { return false; }
            room.filterName = room.name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const names = room.filterName.split(/[ -]+/);
            return queries.every((query) => {
                return names.some((name, index) => {
                    if (name.length && name.indexOf(query) !== -1) { names[index] = ''; return true; }
                    return false;
                });
            });
        });
    }

    public async searchBubblesByTag(searchedTagName: string, exactMatch?: boolean): Promise<Bubble[]> {
        if (this.bubbleTagService) return this.bubbleTagService?.findBubblesByTag(searchedTagName, exactMatch);
        return [];
    }

    public async startMediaEchoTest(testLength: number = 15000): Promise<void> {
        try {
            // Call conference portal API to start the pilot test (https://api.openrainbow.org/conference/#api-conference_v2-startPilotTest)
            const bubbleId = await this.webConferenceService?.startMediaEchoTestV2();

            // Retrieve the bubble created on server side for the test
            const testBubble = await this.getBubble(bubbleId);
            let bubbleConference;
            try {
                // Join the test bubble
                bubbleConference = await this.webConferenceService?.joinEchoTestWebConference(testBubble);

                // Subscribe to all services available
                if (bubbleConference.services?.length) {
                    bubbleConference.services.forEach(async (service) => {
                        await this.webConferenceService.subscribeToEchoTestService(bubbleConference, service.id);
                    });
                }
                //await this.webConferenceService.waitWebConferenceConnection(bubbleConference);
            } catch (error) {
                this.logger.error(`[bubbleService] startMediaEchoTest -- join failed -- ${error.message}`);
                await this.webConferenceService?.stopWebConference(testBubble);
            }

            // Wait a bit for the test
            await UtilitiesService.getInstance().asyncTimeout(testLength);

            // Stop at some point
            await this.webConferenceService?.stopWebConference(testBubble);

        } catch (error) {
            this.logger.error(`[bubbleService] startMediaEchoTest -- FAILURE -- ${error.message}`);
        }
    }

    /**
     * Get all invitations for which the connected user is invited to join a bubble.
     * This means finding all the bubbles  where this user has an "invited" status
     */
    public getBubbleInvitations(): BubbleInvitation[] {
        const rooms: Bubble[] = this.getBubblesFromCache();
        let roomsInvitations: BubbleInvitation[] = rooms as BubbleRB[];
        roomsInvitations = roomsInvitations.filter((bubble: BubbleRB) => {
            return (bubble.bubbleConnectedUser.status === BubbleUserStatus.INVITED
                && bubble.description !== 'Rainbow_OutlookCreation_InternalUseOnly'
                && !bubble.isWebinarBubble());
        });

        return roomsInvitations;
    }

    /**
     * return an array of all room where user is currently invited to participants (in "invited" state)
     */
    public getWebinarInvitations(): Bubble[] {
        return this.getBubblesFromCache().filter((bubble: BubbleRB) => {
            return (bubble.bubbleConnectedUser.status === BubbleUserStatus.INVITED
                && bubble.isWebinarBubble()
                && bubble.webinarId !== null);
        });
    }

    public getRoomsByContainerId(containerId: string): Bubble[] { return this.getBubblesFromCache().filter((room: BubbleRB) => { return room.containerId === containerId; }); }

    /***
    * GET ALL ROOMS ORDER BY DATE OR NAME
    * DEFAULT ORDER IS BY NAME
    */
    public getOrderedRooms(order?: string): Bubble[] {
        const bubbles = this.getBubblesFromCache().filter(bubble => bubble.bubbleConnectedUser);
        switch (order) {
            case 'lastActivityDate':
                return bubbles.sort((bubbleA: BubbleRB, bubbleB: BubbleRB) => { return bubbleB.lastActivityDate.getTime() - bubbleA.lastActivityDate.getTime(); });
            case 'creationDate':
                return bubbles.sort((bubbleA: BubbleRB, bubbleB: BubbleRB) => { return bubbleB.creationDate.getTime() - bubbleA.creationDate.getTime(); });
            default:
                return bubbles.sort((bubbleA: BubbleRB, bubbleB: BubbleRB) => { return bubbleA.name.localeCompare(bubbleB.name); });
        }
    }

    /***
    * GET MY ROOMS ORDER BY DATE OR NAME
    * DEFAULT ORDER IS BY NAME
    */
    public getMyOrderedRooms(order: string): Bubble[] {
        const bubbles = this.getMyBubbles().filter(bubble => bubble.bubbleConnectedUser);
        switch (order) {
            case 'lastActivityDate':
                return bubbles.sort((bubbleA: BubbleRB, bubbleB: BubbleRB) => { return bubbleB.lastActivityDate.getTime() - bubbleA.lastActivityDate.getTime(); });
            case 'creationDate':
                return bubbles.sort((bubbleA: BubbleRB, bubbleB: BubbleRB) => { return bubbleB.creationDate.getTime() - bubbleA.creationDate.getTime(); });
            default:
                return bubbles.sort((bubbleA: BubbleRB, bubbleB: BubbleRB) => { return bubbleA.name.localeCompare(bubbleB.name); });
        }
    }

    // ------------------------------------------------------------
    //                      ROOMS SERVER ACCESSORS
    // ------------------------------------------------------------

    /*
    * API used to update the room from the server but do not send any presences or events; Only update of the room via RX
    */
    public async updateRoomFromServer(bubble: Bubble): Promise<void> {
        this.logger.info(`[bubbleService] updateRoomFromServer (room: ${bubble.dbId})`);
        try {
            let url = `${this.portalURL}rooms/${bubble.dbId}?format=full&unsubscribed=true`;
            if (this.context === 'webinar') url += '&context=webinar';
            const response = await fetch(url, { headers: this.authService.getRequestHeader() });
            const bubbleData = (await response.json()).data;
            await this.getAllBubblesParticipants([bubbleData]);
            this.updateRoomData(bubble as BubbleRB, bubbleData);
            bubble.sendEvent(BubbleEvents.ON_DATA_UPDATED);
        }
        catch (error) {
            this.logger.error(`[bubbleService] updateRoomFromServer (room: ${bubble.dbId}) -- FAILURE -- ${error.message}`);
        }
    }

    private updateRoomData(bubble: BubbleRB, newRoomData: any): void {
        let sendRoomStatusEvent = false;
        // Update name and description
        bubble.name = newRoomData.name;
        bubble.description = newRoomData.topic; // Warning: topic = field name provided by Server

        // Update conference property data
        bubble.conference = newRoomData.conference; // BIG HACK for RQRAINB-5201

        // Get the previous status of the connected user
        const oldConnectedUserStatus: BubbleUserStatus = bubble.bubbleConnectedUser?.status;

        // Update room participants
        bubble.users = [];
        newRoomData.users.forEach((userData: any) => {
            const contact = this.contactService.getContactFromCacheById(userData.userId);
            if (contact) {
                const newUserBubble: BubbleUser = { user: contact, status: userData.status, date: new Date(userData.additionDate), role: userData.privilege };
                bubble.users.push(newUserBubble);
                // If The current bubble user is the connected user, mandatory to update bubbleConnectedUser !
                if (this.contactService.isUserContact(contact)) {
                    bubble.bubbleConnectedUser = newUserBubble;
                    if (oldConnectedUserStatus !== newUserBubble.status) {
                        sendRoomStatusEvent = true;
                    }
                }
            }
        });
        this.refreshMemberAndOrganizerLists(bubble);

        // Update room avatar
        if (newRoomData.lastAvatarUpdateDate && (newRoomData.lastAvatarUpdateDate !== bubble.lastAvatarUpdate)) {
            bubble.lastAvatarUpdate = newRoomData.lastAvatarUpdateDate;
            const updateToken = MD5(bubble.lastAvatarUpdate);
            bubble.avatar = `${config.restServerUrl}/api/room-avatar/${bubble.dbId}?size=512&update=${updateToken}`;
        }
        else if (!newRoomData.lastAvatarUpdateDate && bubble.lastAvatarUpdate && bubble.avatar) {
            bubble.lastAvatarUpdate = newRoomData.lastAvatarUpdateDate;
            bubble.avatar = null;
        }
        bubble.updateAvatarInfo();

        // Update room guests
        bubble.guestEmails = newRoomData.guestEmails;
        bubble.guestPhoneNumbers = (newRoomData.conference?.guestPhoneNumbers) ? newRoomData.conference.guestPhoneNumbers : [];

        if (newRoomData["allParticipants"] && !bubble.usersReady.value) {
            bubble.usersReady.next(true);
        }

        //send event if the status in the bubble has changed
        if (sendRoomStatusEvent) {
            const actionEvent: BubbleServiceEvents = this.getEventFromBubbleStatus(bubble.bubbleConnectedUser.status);
            this.sendEvent(actionEvent, { bubble: bubble, bubbleUser: bubble.bubbleConnectedUser });
        }
    }

    public async getServerBubble(roomId: string): Promise<BubbleRB> {
        try {
            this.logger.info(`[bubbleService] getServerBubble(${roomId})`);
            let url = `${this.portalURL}rooms/${roomId}?format=full&unsubscribed=true`;
            if (this.context === 'webinar') url += `&context=webinar`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const roomData = (await response.json()).data;
            this.logger.info(`[bubbleService] getServerBubble(${roomId}) -- SUCCESS`);
            const roomsData = await this.getAllBubblesParticipants([roomData]);
            return this.createOrUpdateBubbleFromData(roomsData[0]);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getServerBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Get room data from server then create and store the Room instance
     * @param bubbleJid - the jid of the room to get
     * @param sendPresence - (default : true) When set to false the initial presence is not sent
     * @param nbUsersToKeep - number of user to keep in room data
     */
    private getServerBubbleByJid(bubbleJid: string, sendPresence?: boolean, nbUsersToKeep?: number): Promise<BubbleRB> {
        if (!this.getBubbleByJidPromises[bubbleJid]) {
            this.getBubbleByJidPromises[bubbleJid] = (async (): Promise<BubbleRB> => {
                try {
                    let url = `${this.portalURL}rooms/jids/${bubbleJid}?format=full&webinar=true&${nbUsersToKeep ? 'nbUsersToKeep=' + nbUsersToKeep : ''}`;
                    if (this.context === 'webinar') { url += '&context=webinar'; }
                    const headers = this.authService.getRequestHeader();
                    const response = await fetch(url, { method: 'GET', headers });
                    if (!response.ok) { throw (response); }
                    const roomData = (await response.json()).data;
                    await this.getMinimalBubbleUsers([roomData]);
                    this.logger.info(`[bubbleService] getServerBubbleByJid(${bubbleJid}) -- SUCCESS`);
                    delete this.getBubbleByJidPromises[bubbleJid];
                    const doNotSendPresence = sendPresence !== undefined && !sendPresence;
                    const bubble = this.createOrUpdateBubbleFromData(roomData, doNotSendPresence, true);
                    return bubble;
                }
                catch (error) {
                    const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getServerBubbleByJid');
                    this.logger.error(`[bubbleService] ${errorInfo.message}`);
                    delete this.getBubbleByJidPromises[bubbleJid];
                    throw errorInfo.error;
                }
            })();
        }
        return this.getBubbleByJidPromises[bubbleJid];
    }

    public async getServerBubblesByIds(bubbleIds: string[], sendPresence?: boolean, webinars?: boolean): Promise<void> {
        try {
            if (bubbleIds.length === 0) return;
            let url = `${this.portalURL}rooms/ids?format=full&userId=${this.userContact.dbId}&needIsAlertNotificationEnabled=false&nbUsersToKeep=4`;
            if (webinars) url += '&webinar=true';
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ roomIds: bubbleIds }) });
            if (!response.ok) { throw (response); }
            const bubblesData = (await response.json()).data;
            await this.getMinimalBubbleUsers(bubblesData);
            this.logger.info(`[bubbleService] getServerBubblesByIds -- SUCCESS`);
            const doNotSendPresence = sendPresence !== undefined && !sendPresence;
            bubblesData.forEach((bubbleData: any) => {
                try { this.createOrUpdateBubbleFromData(bubbleData, doNotSendPresence, true); }
                catch { this.logger.error(`[bubbleService] getServerBubblesByIds -- create bubble ${bubbleData.id} failure`); }
            });
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getServerBubblesByIds');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public areAllServerBubblesReady(status: string): boolean {
        return this.preload[status];
    }

    public loadAllAcceptedBubblesAtStartUp(): void {
        setTimeout(() => {
            if (!this.starting) return;
            this.loadAllAcceptedBubbles();
        }, 25000);
    }

    public async loadAllAcceptedBubbles(): Promise<void> {
        if (this.loadAllAcceptedBubblesPromise) { return this.loadAllAcceptedBubblesPromise; }
        this.loadAllAcceptedBubblesPromise = this.getAllServerBubbles('accepted');

        this.loadAllAcceptedBubblesPromise.catch(() => {
            this.loadAllAcceptedBubblesPromise = null;
        });

        return this.loadAllAcceptedBubblesPromise;
    }

    private async getServerBubblesDataPage(status: string, offset: number, limit: number): Promise<any> {
        try {
            // Get bubbles data
            const startDate = performance.now();
            let url = `${this.portalURL}rooms?format=full&userId=${this.userContact.dbId}&needIsAlertNotificationEnabled=false&sortField=lastActivityDate&offset=${offset}&limit=${limit}&webinar=false&status=${status}&nbUsersToKeep=4`;
            if (this.context === 'webinar') { url += '&context=webinar'; }
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData = await response.json();
            const bubblesData = responseData.data;
            const total = responseData.total;
            // Get minimal contact subset
            await this.getMinimalBubbleUsers(bubblesData);
            // Create bubbles
            const bubbles = bubblesData.map((roomData: any) => { return this.createOrUpdateBubbleFromData(roomData, true, true); });
            const endDate = performance.now();
            this.logger.info(`[bubbleService] getServerBubblesDataPage-- ${status} --offset ${offset} --success --get ${bubbles.length} bubbles from ${responseData.total} --${Math.round(endDate - startDate)} ms`);
            return { rooms: bubbles, total };
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `getServerBubblesDataPage(${status}, ${offset})`);
            throw errorInfo.error;
        }
    }

    private async getMinimalBubbleUsers(bubblesData: any[]): Promise<void> {
        const bubbleUsersData = bubblesData.reduce((data: any, roomData: any) => {
            if (!roomData.lastAvatarUpdateDate) data.usersData = data.usersData.concat(roomData.users);
            data.creatorsData.push(roomData.creator);
            return data;
        }, { usersData: [], creatorsData: [] });
        await this.fetchBubbleUsersContacts(bubbleUsersData);
    }

    private async fetchBubbleUsersContacts(bubbleUsersData: any, bubble?: BubbleRB): Promise<void> {
        try {
            const startDate = performance.now();
            const jids: any = {};
            const tvDbIds: any = {};
            bubbleUsersData.usersData.forEach((bubbleUserData: any) => {
                if (bubbleUserData.jid_im && bubbleUserData.jid_im.indexOf("tv_") === 0 && bubbleUserData.userId) {
                    if (!tvDbIds[bubbleUserData.userId]) { tvDbIds[bubbleUserData.userId] = true; }
                }
                else if (!jids[bubbleUserData.jid_im]) jids[bubbleUserData.jid_im] = true;
            });
            const missingRoomsUsersJids = Object.keys(jids).filter((jid: string) => { const contact = this.contactService.getContactFromCacheByJid(jid); return !contact || contact.temp; });
            const missingRoomsTvsDbIds = Object.keys(tvDbIds).filter((dbId: string) => { const contact = this.contactService.getContactFromCacheById(dbId); return !contact || contact.temp; });

            const nbContactToFetch = missingRoomsUsersJids.length;
            if (nbContactToFetch) {
                const arrays = [];
                while (missingRoomsUsersJids.length > 0) { arrays.push(missingRoomsUsersJids.splice(0, 1000)); }
                await Promise.all(arrays.map((jidArray: any) => { return this.contactService.getContactsByJids(jidArray); }));
            }

            if (missingRoomsTvsDbIds.length) {
                await allSettled(missingRoomsTvsDbIds.map((dbId: any) => { return this.contactService.getContactByDBId(dbId); }));
            }

            await Promise.all(bubbleUsersData.creatorsData.map((creatorId: any) => { return this.contactService.getContactByDBId(creatorId); }));

            const endDate = performance.now();
            this.logger.info(`[bubbleService] fetchBubbleUsersContacts - ${bubble ? bubble.dbId : ''} - get ${nbContactToFetch} participants-- ${Math.round(endDate - startDate)} ms`);
        }
        catch (error) {
            this.logger.error(`[bubbleService] fetchBubbleUsersContacts ${error.message} `);
        }
    }

    public async getBubbleUsers(bubble: BubbleRB, role: 'ALL' | 'USER' | 'ORGANIZER' = 'ALL'): Promise<BubbleUser[]> {
        await this.getAllBubbleUsers(bubble);
        switch (role) {
            case 'USER': return bubble.members;
            case 'ORGANIZER': return bubble.organizers;
            default: return bubble.users;
        }
    }

    /*
    *   API used to get all the room users, used by conversation service to update the list of users when a room conversation is opened
    */
    public async getAllBubbleUsers(bubble: BubbleRB): Promise<void> {
        if (bubble.usersReady.value) return;
        if (!this.getBubbleByJidPromises[bubble.dbId]) {
            this.getBubbleByJidPromises[bubble.dbId] = (async (): Promise<BubbleRB> => {
                try {
                    this.logger.info(`[bubbleService] getAllBubbleUsers for bubble ${bubble.dbId}`);

                    // Get all users data from server
                    const usersRequests: Promise<any>[] = [];
                    for (let offset = 0; offset < bubble.nbParticipants; offset += 100) { usersRequests.push(this.getBubbleParticipantPage(bubble.dbId, offset, 100)); }
                    const usersPages = await Promise.all(usersRequests);
                    const usersData = usersPages.reduce((data: any[], usersPage: any) => { data = data.concat(usersPage.data); return data; }, []);

                    // Fetch missing contacts
                    await this.fetchBubbleUsersContacts({ usersData, creatorsData: [] }, bubble);

                    let sendUserStatusUpdateEvent = false;
                    // Get the previous status of the connected user
                    const oldConnectedUserStatus: BubbleUserStatus = bubble.bubbleConnectedUser.status;

                    // Update room participants
                    bubble.users = [];
                    usersData.forEach((userData: any) => {
                        const contact = this.contactService.getContactFromCacheById(userData.userId);
                        if (contact) {
                            const newUserBubble: BubbleUser = { user: contact, status: userData.status, date: new Date(userData.additionDate), role: userData.privilege, invitingUserId: userData.invitingUserId };
                            bubble.users.push(newUserBubble);
                            // Check if the current bubble user is the connected user
                            if (this.contactService.isUserContact(contact)) {
                                // Not sure that it is useful here to update bubbleConnectedUser
                                bubble.bubbleConnectedUser = newUserBubble;
                                // If the status of the connected user in this bubble is different, event must be sent
                                if (oldConnectedUserStatus === BubbleUserStatus.INVITED && newUserBubble.status === BubbleUserStatus.ACCEPTED) {
                                    sendUserStatusUpdateEvent = true;
                                }
                            }
                        }
                    });
                    this.refreshMemberAndOrganizerLists(bubble);
                    bubble.usersReady.next(true);
                    this.logger.info(`[bubbleService] getAllBubbleUsers for bubble ${bubble.dbId} ${bubble.users?.length} -- success`);
                    bubble.sendEvent(BubbleEvents.ON_DATA_UPDATED);
                    delete this.getBubbleByJidPromises[bubble.dbId];

                    if (sendUserStatusUpdateEvent) {
                        //the bubble is going from INVITED to ACCEPTED state, send user update event and check for active conference
                        this.updateMyUserToAcceptedStatus(bubble);
                    }
                    return bubble;
                }
                catch (error) {
                    const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAllBubbleUsers');
                    if (!(config.sdk && (bubble.visibility === BubbleVisibility.PUBLIC) && errorInfo.error.errorDetailsCode === 403000)) this.logger.error(`[bubbleService] ${errorInfo.message}`);
                    delete this.getBubbleByJidPromises[bubble.dbId];
                }
            })();
        }
        await this.getBubbleByJidPromises[bubble.dbId];
    }

    /*
     * WARNING : Do not add try/catch here, the error has to be propagated
     * SDK CONSTRAINT : For public bubble (want to hide getUsers message)
     */
    private async getBubbleParticipants(bubbleData: any) {
        const limit = 100;
        const bubbleId = bubbleData.id;
        const total: number = bubbleData.nbParticipants;
        let usersData: any[] = bubbleData.users.concat();

        if (total > limit) {
            // For bubbles where user count is over 100 and need this method, we begin from 0 to make sure that we have all contacts
            usersData = [];
            const requestCount = total / limit + 1;
            let offset = 0;
            const requests: Promise<any>[] = [];
            for (let i = 1; i < requestCount; i++) { requests.push(this.getBubbleParticipantPage(bubbleId, offset, limit)); offset += limit; }
            const pages = await Promise.all(requests);
            pages.forEach((page: any) => { usersData = usersData.concat(page.data); });
            bubbleData.users = usersData;
        }
        return usersData;
    }

    /*
     * WARNING : Do not add try/catch here, the error has to be propagated
     * SDK CONSTRAINT : For public bubble (want to hide getUsers message)
     */
    private async getBubbleParticipantPage(roomId: string, offset: number, limit: number) {
        let url = `${this.portalURL}rooms/${roomId}/users?format=full&offset=${offset}&limit=${limit}`;
        if (this.context === 'webinar') url += '&context=webinar';
        const headers = this.authService.getRequestHeader();
        const response: Response = await fetch(url, { method: 'GET', headers });
        if (!response.ok) { throw (response); }
        return await response.json();
    }

    private async getAllBubblesParticipants(bubblesData: any): Promise<any> {
        try {
            const startDate = performance.now();
            const bubblesUsersArrays = await Promise.all(bubblesData.map((bubbleData: any) => { return this.getBubbleParticipants(bubbleData); }));
            const bubblesUsersJids = bubblesUsersArrays.reduce((jids: any, bubbleUsers: any[]) => {
                bubbleUsers.forEach((bubbleUser: any) => { if (!jids[bubbleUser.jid_im]) jids[bubbleUser.jid_im] = true; });
                return jids;
            }, {});
            const missingBubblesUsersJids = Object.keys(bubblesUsersJids).filter((jid: string) => {
                const contact = this.contactService.getContactFromCacheByJid(jid);
                return !contact || contact.temp;
            });

            if (missingBubblesUsersJids?.length) {
                if (missingBubblesUsersJids.length > 1000) {
                    const arrays = [];
                    while (missingBubblesUsersJids.length > 0) { arrays.push(missingBubblesUsersJids.splice(0, 1000)); }
                    await Promise.all(arrays.map((jidArray: any) => { return this.contactService.getContactsByJids(jidArray); }));
                }
                else {
                    await this.contactService.getContactsByJids(missingBubblesUsersJids);
                }
            }

            await Promise.all(bubblesData.map((roomData: any) => { return this.contactService.getContactByDBId(roomData.creator); }));

            const endDate = performance.now();
            this.logger.info(`[bubbleService] getAllBubblesParticipants - get ${missingBubblesUsersJids.length} participants in ${Math.round(endDate - startDate)} ms`);
            bubblesData["allParticipants"] = true;
            return bubblesData;
        }
        catch (error) {
            this.logger.error(`[bubbleService] ${error.message}`);
            throw error;
        }
    }

    private createOrUpdateBubbleFromData(bubbleData: any, doNotSendPresence: boolean = false, doNotSendBubbleEvent: boolean = false): BubbleRB {
        let sendBubbleStatusEvent = false;
        const bubble: BubbleRB = this.bubbles[bubbleData.id] ? this.bubbles[bubbleData.id] : BubbleRB.create();
        bubble.update(bubbleData);

        // Store confSession in update case
        const currentConferenceSession = this.bubbles[bubbleData.id] ? this.bubbles[bubbleData.id].getCurrentConferenceSession() : null;

        // Get owner contact
        const owner = this.contactService.dbContacts[bubbleData.creator];
        if (!owner) this.logger.warn(`[bubbleService] createOrUpdateBubbleFromData -- failure -- Impossible to find owner of room ${bubbleData.name} --- ignored`);
        else if (bubbleData.isOwnedByGroup) {
            this.logger.info(`[bubbleService] createOrUpdateBubbleFromData -- Room created for hunting group -- ${bubbleData.name} -- make me the owner`);
            bubble.owner = this.contactService.userContact;
            bubble.groupId = bubbleData.groupId;
        }
        else {
            bubble.owner = owner;
        }

        // Get the previous status of the connected user
        const oldConnectedUserStatus: BubbleUserStatus = bubble?.bubbleConnectedUser?.status;

        // Add room participants
        //in case it's all participants, or we have no or less participants than received
        //otherwise we remove the participants from the bubble that we've already got from the server
        if (!bubble.usersReady.value && (bubbleData["allParticipants"] || !bubble.users?.length || (bubble.users.length < bubbleData.users.length))) {
            bubble.users = [];
            bubbleData.users.forEach((userData: any) => {
                const contact = this.contactService.getContactFromCacheById(userData.userId);
                if (contact) {
                    const newUserBubble: BubbleUser = { user: contact, status: userData.status, date: new Date(userData.additionDate), role: userData.privilege, invitingUserId: userData.invitingUserId };
                    bubble.users.push(newUserBubble);
                    // Check if the current bubble user is the connected user
                    if (this.contactService.isUserContact(contact)) {
                        // Not sure that it is useful here to update bubbleConnectedUser
                        bubble.bubbleConnectedUser = newUserBubble;
                        // If the status of the connected user in this bubble is different, event must be sent
                        if (oldConnectedUserStatus !== newUserBubble.status) {
                            sendBubbleStatusEvent = true;
                        }
                    }
                }
            });

            // Add room guests
            bubble.guestEmails = bubbleData.guestEmails;
            bubble.guestPhoneNumbers = (bubbleData.conference?.guestPhoneNumbers) ? bubbleData.conference.guestPhoneNumbers : [];
        }
        else {
            //users are already updated, but check and update their status if needed
            bubbleData.users.forEach((userData: any) => {
                const contact = this.contactService.getContactFromCacheById(userData.userId);
                if (contact) {
                    bubble.users.forEach((roomUser) => {
                        if (roomUser.user?.dbId === contact.dbId) {
                            roomUser.status = userData.status;
                            if (userData?.privilege) { roomUser.role = userData.privilege; }
                        }
                    });
                    if (this.contactService.isUserContact(contact)) {
                        // Check if the status of the connected user in this bubble is different
                        if (oldConnectedUserStatus !== userData.status) {
                            sendBubbleStatusEvent = true;
                        }
                    }
                }
            });
        }

        this.refreshMemberAndOrganizerLists(bubble);

        //the room has all participants ready
        if (bubbleData["allParticipants"] && !bubble.usersReady.value) {
            bubble.usersReady.next(true);
        }

        // Update room avatar
        bubble.updateAvatarInfo();
        if (bubbleData.lastAvatarUpdateDate) {
            bubble.avatar = config.restServerUrl + "/api/room-avatar/" + bubbleData.id + "?size=512&update=" + MD5(bubbleData.lastAvatarUpdateDate);
        }

        // Handle webinar info
        if (bubble.isWebinarBubble()) { bubble.webinarId = bubble.conference.webinar.id; }

        // Store room in service
        this.bubbles[bubble.dbId] = bubble;
        this.bubblesByJids[bubble.jid] = bubble;

        if (bubble.bubbleConnectedUser) {

            if (bubble.bubbleConnectedUser.status === BubbleUserStatus.ACCEPTED && bubble.isActive && !bubble.ready && !doNotSendPresence) {
                this.sendInitialRoomPresenceSync(bubble);
            }

            //send event if the status in the bubble has changed
            if (!doNotSendBubbleEvent && sendBubbleStatusEvent) {
                const actionEvent: BubbleServiceEvents = this.getEventFromBubbleStatus(bubble.bubbleConnectedUser.status);
                this.sendEvent(actionEvent, { bubble: bubble, bubbleUser: bubble.bubbleConnectedUser });
            }

            if (doNotSendPresence && !bubble.ready) {
                //if room is not active, do not update the init presence ack
                //only do it for bubbles that I've accepted
                if (bubble.bubbleConnectedUser.status === BubbleUserStatus.ACCEPTED) {
                    bubble.ready = bubble.isActive;
                }
                doNotSendBubbleEvent = false; // MCO event envoyé pour la reco des conv avec les bulles écrasées
            }
        }

        // Send update event and room initial presence
        if (!doNotSendBubbleEvent) bubble.sendEvent(BubbleEvents.ON_DATA_UPDATED);

        // Send conference event
        let sendConferenceEventType = '';
        if (this.bubbles[bubble.dbId]) {

            // Update the room conference object and send related events for the rooms
            // Check if room has active conference
            if (currentConferenceSession && !bubble.getCurrentConferenceSession()) {
                // Conference is no longer active, send event
                sendConferenceEventType = "stopEvent";
            }
            else if (!currentConferenceSession && bubble.getCurrentConferenceSession()) {
                // Conference is active, send event
                sendConferenceEventType = "startEvent";
            }
        }
        if (sendConferenceEventType) {
            if (sendConferenceEventType === "stopEvent") bubble.sendEvent(BubbleEvents.ON_CONFERENCE_STOP_EVENT);
            else bubble.sendEvent(BubbleEvents.ON_CONFERENCE_START_EVENT);
        }

        return bubble;
    }

    private refreshMemberAndOrganizerLists(bubble: BubbleRB): void {
        // Clear both lists :
        bubble.organizers = [];
        bubble.members = [];

        bubble.users.forEach((user: BubbleUser) => {
            if (user.status === BubbleUserStatus.ACCEPTED || user.status === BubbleUserStatus.INVITED || (bubble.owner && user.user.jid === bubble.owner.jid)) {
                if (user.role === BubbleUserRole.ORGANIZER) { bubble.organizers.push(user); }
                else { bubble.members.push(user); }
            }
        });
    }

    // ------------------------------------------------------------
    //                      BUBBLE AVATAR STUFF
    // ------------------------------------------------------------

    /**
     * Update the bubble avatar
     * @param bubble - the bubble
     * @param avatarImg - the image to be set
     */
    public async setBubbleAvatar(bubble: Bubble, avatarImg: any, width: number = 512, height: number = 512): Promise<Bubble> {
        if (!avatarImg) return bubble;
        try {
            const resizedImage = await resizeImage(avatarImg, width, height);
            const binaryData = getBinaryData(resizedImage);
            let url = `${this.portalURL}rooms/${bubble.dbId}/avatar`;
            if (this.context === 'webinar') url += '?context=webinar';
            const headers = this.authService.getPostHeader("image/" + binaryData.type);
            const response: Response = await fetch(url, { method: 'POST', headers, body: binaryData.data });
            if (!response.ok) throw (response);
            await response.json();
            this.logger.info('[bubbleService] setBubbleAvatar -- SUCCESS');
            bubble.avatar = `${config.restServerUrl}/api/room-avatar/${bubble.dbId}?size=512&rand=${randomString()}`;
            return bubble;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setBubbleAvatar');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Delete the bubble avatar
     * @param bubble - the bubble
     */
    public async deleteBubbleAvatar(bubble: Bubble): Promise<void> {
        try {
            let url = `${this.portalURL}rooms/${bubble.dbId}/avatar`;
            if (this.context === 'webinar') url += '?context=webinar';
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) throw (response);
            bubble.avatar = null;
            this.logger.info('[bubbleService] deleteBubbleAvatar -- SUCCESS');
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteBubbleAvatar');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*
        PUBLIC APIS RELATED TO BUBBLE CREATION AND MANAGEMENT
    */

    /**
     * Create a new bubble.
     * This API also sends BubbleServiceEvents.ON_BUBBLE_CREATED event.
     * @param bubbleName - The name of the bubble
     * @param createBubbleData - Bubble properties that can be specified when it is created.
     */
    public async createBubble(bubbleName: string = "", createBubbleData: BubbleCreationData = {}): Promise<BubbleRB> {
        try {
            this.createBubbleInProgress = this.createBubbleInternal(bubbleName, createBubbleData);
            const bubble = await this.createBubbleInProgress;
            this.createBubbleInProgress = null;
            let hasError = false;
            let invalidUsersIds = [];
            if (createBubbleData.contacts?.length || createBubbleData.guestEmails?.length) {
                try {
                    invalidUsersIds = await this.addUsersToBubble(bubble, createBubbleData.contacts, createBubbleData.guestEmails, createBubbleData.role);
                    hasError = Boolean(invalidUsersIds?.length);
                }
                catch {
                    hasError = true;
                    this.logger.error(`[bubbleService] createBubble -- could not add users inside`);
                }
            }

            // Send event that bubble has been created (with some errors or not)
            this.sendEvent(BubbleServiceEvents.ON_BUBBLE_CREATED, { bubble, invitedUsersError: hasError, invalidUsersIds });
            return bubble;
        }
        catch (error) {
            this.createBubbleInProgress = null;
            throw error;
        }
    }

    /**
    * Invite users and guests to the bubble; The guests will receive an email to join the bubble;
    * @param bubble - the bubble to invite to
    * @param users - the users to add bubble
    * @param emails - list of emails to invite to the bubble
    * @param role - BubbleUserRole to be applied to all invited users
    */
    public async inviteUsersToBubble(bubble: Bubble, users: User[] = [], emails: string[] = [], role?: BubbleUserRole): Promise<Bubble> {
        if (!bubble) { throw new Error("[bubbleService] inviteUsersToBubble -- bubble not found"); }

        if (users?.length || emails?.length) {
            try { await this.addUsersToBubble(bubble as BubbleRB, users, emails, role); }
            catch { this.logger.error(`[bubbleService] inviteUsersToBubble -- could not add users inside`); }
        }
        return bubble;
    }

    /**
     * Add rainbow TVs to the bubble;
     * @param bubble - the bubble to invite to
     * @param androidTVs - the contacts of type Rainbow TV
     * @returns resolves once it's done
     */
    public async addRainbowTVsToBubble(bubble: Bubble, androidTVs: User[]): Promise<Bubble> {
        try {
            if (!bubble) { throw new Error("[bubbleService] inviteUsersToBubble -- bubble not found"); }
            // Create the add user promises
            const addBubbleAndroidTVPromises = [];
            androidTVs.forEach((androidTV: User) => {
                if (androidTV) addBubbleAndroidTVPromises.push(bubble.addUser(androidTV, BubbleUserRole.ORGANIZER, 'androidtv', true));
            });
            await Promise.all(addBubbleAndroidTVPromises);

            this.logger.info("[bubbleService] addRainbowTVsToBubble success");
            return bubble;
        }
        catch (error) {
            const errorMessage = "addRainbowTVsToBubble failure: " + error.message;
            this.logger.error("[bubbleService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
    * Add user to the bubble; Can be used to directly add user without invitation or as a organizer
    * @param bubble - the bubble
    * @param user - the user to add
    * @param reason - [optional] the reason to add
    * @param role - the role to be set ('user' or 'organizer'), by default is 'user'
    * @param  withoutInvitation - if TRUE, the user will be added without invitation before
    */
    public async addUserToBubble(bubble: Bubble, user: User, reason: string = null, role: BubbleUserRole = null, withoutInvitation: boolean = false): Promise<Bubble> {
        try {
            if (!bubble) { throw new Error("[bubbleService] addUserToBubble -- bubble not found"); }
            const userContact = user as Contact;

            const isUserInvitedOrAccepted = (bubble as BubbleRB).users.some((bubbleUser) => {
                return ((bubbleUser.status === BubbleUserStatus.INVITED
                    || bubbleUser.status === BubbleUserStatus.ACCEPTED)
                    && bubbleUser.user.dbId === userContact.dbId);
            });

            if (isUserInvitedOrAccepted) {
                this.logger.debug(`[bubbleService] user: ${userContact.dbId} already invited or accepted, will not be added`);
                return bubble;
            }

            const bubbleUsersCurrentNb = (bubble as BubbleRB).users.filter((user) => { return user.status !== BubbleUserStatus.DELETED; }).length;

            // Reject not allowed operations
            if (bubbleUsersCurrentNb >= this.maxBubblesUsers) {
                const profileError: any = new Error("addRoomUser failure - Not Allowed : Participants max limit has been reached: " + this.maxBubblesUsers);
                profileError.status = profileError.errorDetailsCode = "403";
                profileError.errorDetailsLabel = `MaximumNumberRoomUsersReach`;
                profileError.errorDetailsCode = `403620`;
                this.logger.error("[bubbleService] " + profileError.message);
                throw profileError;
            }

            // Manage status invitation
            const userStatus = (withoutInvitation) ? BubbleUserStatus.ACCEPTED : BubbleUserStatus.INVITED;
            const userReason = reason ? reason : "JustForFun";
            const userRole = role ? role : BubbleUserRole.USER;

            const url = `${this.portalURL}rooms/${bubble.dbId}/users`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ userId: userContact.dbId, privilege: userRole, reason: userReason, status: userStatus });
            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw (response);

            this.logger.info(`[bubbleService] addUserToBubble ${userContact.nameForLogs} to room ${bubble.dbId} (${bubble.getNameForLogs()}) success`);

            const userData = (await response.json()).data;
            const bubbleUser: BubbleUser = (bubble as BubbleRB).getUserByJid(userData.jid_im);
            if (bubbleUser) {
                bubbleUser.status = userData.status;
                bubbleUser.date = new Date();
                bubbleUser.role = userData.privilege;
            }
            else {
                (bubble as BubbleRB).users.push({ user, status: userStatus, date: new Date(), role: userRole } as BubbleUser);
            }

            this.refreshMemberAndOrganizerLists(bubble as BubbleRB);
            return bubble;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addUserToBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // ------------------------------------------------------------
    //                      UPDATE FUNCTIONS
    // ------------------------------------------------------------

    /**
     * Update all users to "moderators" role; CAN BE USED ONLY BY THE OWNER
     * @param bubble - the bubble to update
     */
    public async promoteAllUsers(bubble: Bubble): Promise<Bubble> {
        try {
            if (!bubble) { throw new Error("[bubbleService] promoteAllUsers -- bubble not found"); }
            if (!bubble.isConnecteUserOwner()) { throw new Error("[bubbleService] promoteAllUsers -- API allowed only for the owner"); }

            const url = `${this.portalURL}rooms/${bubble.dbId}/promote-all`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw (response);

            this.logger.info(`[bubbleService] promoteAllUsers in bubble ${bubble.dbId} (${bubble.getNameForLogs()}) success`);

            const roomData = (await response.json()).data;
            this.createOrUpdateBubbleFromData(roomData);

            return bubble;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'promoteAllUsers');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Update all users to "user" role; CAN BE USED ONLY BY THE OWNER
     * @param bubble - the bubble to update
     */
    public async demoteAllUsers(bubble: Bubble): Promise<Bubble> {
        try {
            if (!bubble) { throw new Error("[bubbleService] demoteAllUsers -- bubble not found"); }
            if (!bubble.isConnecteUserOwner()) { throw new Error("[bubbleService] demoteAllUsers -- API allowed only for the owner"); }

            const url = `${this.portalURL}rooms/${bubble.dbId}/demote-all`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw (response);

            this.logger.info(`[bubbleService] demoteAllUsers in bubble ${bubble.dbId} (${bubble.getNameForLogs()}) success`);

            const roomData = (await response.json()).data;
            this.createOrUpdateBubbleFromData(roomData);

            return bubble;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'demoteAllUsers');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This API allows to activate and deactivate the bubble's lobby
     *  Only the owner of the bubble can use this API
     *  The user must have the feature key BUBBLE_WAITING_ROOM in his service plan
     *  When the lobby is closed, all waiting users are automatically accepted. An event is sent to each bubble organizer about the current lobby state
     * @param bubble - the bubble to update
     */
    public async manageBubbleLobby(bubble: Bubble, activate: boolean): Promise<Bubble> {
        try {
            if (!bubble) { throw new Error("[bubbleService] manageBubbleLobby -- bubble not found"); }
            if (!bubble.isUserOwner) { throw new Error("[bubbleService] manageBubbleLobby -- API allowed only for the owner"); }
            if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.BUBBLE_WAITING_ROOM)) { throw new Error("[bubbleService] manageBubbleLobby -- Feature not allowed for user"); }

            const url = `${this.portalURL}rooms/${bubble.dbId}/lobbies`;
            const headers = this.authService.getPostHeader();
            const data = { hasLobby: activate };
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw (response);

            this.logger.info(`[bubbleService] manageBubbleLobby -- bubble lobby in ${bubble.dbId} (${bubble.getNameForLogs()}) is now ${activate}`);

            const roomData = (await response.json()).data;
            this.createOrUpdateBubbleFromData(roomData);

            return bubble;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'manageBubbleLobby');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This API allows to activate and deactivate the bubble's password protection
     *  Only the owner of the bubble can use this API
     *  The user must have the feature key BUBBLE_PASSWORD in his service plan
     * @param bubble - the bubble to update
     */
    public async manageBubblePassword(bubble: Bubble, activate: boolean): Promise<Bubble> {
        try {
            if (!bubble) { throw new Error("[bubbleService] manageBubblePassword -- bubble not found"); }
            if (!bubble.isUserOwner) { throw new Error("[bubbleService] manageBubblePassword -- API allowed only for the owner"); }
            // if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.BUBBLE_PASSWORD)) { throw new Error("[bubbleService] manageBubbleLobby -- Feature not allowed for user"); }

            const url = `${this.portalURL}rooms/${bubble.dbId}/passwords`;
            const headers = this.authService.getPostHeader();
            const data = { hasPassword: activate };
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw (response);

            this.logger.info(`[bubbleService] manageBubblePassword -- bubble password in ${bubble.dbId} (${bubble.getNameForLogs()}) is now ${activate}`);
            const bubbleData = (await response.json()).data;

            bubble.hasPassword = Boolean(bubbleData.room?.hasPassword);

            //update the password
            if (bubbleData?.publicLink) {
                if (bubble.hasPassword) { bubble.bubblePassword = bubbleData?.publicLink.roomPassword; }
                else { bubble.bubblePassword = ''; }
            }
            return bubble;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'manageBubblePassword');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This API allows to activate and deactivate the bubble's password protection
     *  Only the owner of the bubble can use this API
     *  The user must have the feature key BUBBLE_PASSWORD in his service plan
     * @param bubble - the bubble to update
     */
    public async resetBubblePassword(bubble: Bubble): Promise<Bubble> {
        try {
            if (!bubble) { throw new Error("[bubbleService] resetBubblePassword -- bubble not found"); }
            if (!bubble.isUserOwner) { throw new Error("[bubbleService] resetBubblePassword -- API allowed only for the owner"); }
            // if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.BUBBLE_PASSWORD)) { throw new Error("[bubbleService] manageBubbleLobby -- Feature not allowed for user"); }

            const url = `${this.portalURL}rooms/${bubble.dbId}/passwords/reset`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw (response);

            this.logger.info(`[bubbleService] resetBubblePassword -- bubble password in ${bubble.dbId} (${bubble.getNameForLogs()}) has changed}`);

            const bubbleData = (await response.json()).data;

            bubble.hasPassword = Boolean(bubbleData.room?.hasPassword);

            //update the password
            if (bubbleData?.publicLink) {
                if (bubble.hasPassword) { bubble.bubblePassword = bubbleData?.publicLink.roomPassword; }
                else { bubble.bubblePassword = ''; }
            }
            return bubble;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'resetBubblePassword');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This API allows to search Rainbow bubbles that match a search criteria applied on some name properties such as the name of the bubble
     * or the names of bubble participants for example.
     * One or more name properties can be optionally specified to define the scope of the search.
     * If no scope is specified, the search is performed by default on the name of the bubble only.
     * It is also possible to optionally specify how the list of bubbles returned as results should be sorted.
     * By default, the bubbles list will be sorted by bubble name.
     * @param searchPattern - The search criteria
     * @param options - Options to specify the scope of the search and how the results are to be returned.
     */
    public async searchBubbles(searchPattern: string, options?: BubbleSearchOptions): Promise<BubbleSearchResult[]> {
        let searchByBubbleName: boolean = false;
        let searchByTagsName: boolean = false;
        let searchByParticipantsName: boolean = false;
        let foundBubbles: Bubble[] = [];
        let foundBubblesByName: Bubble[] = [];
        let foundBubblesByTag: Bubble[] = [];
        let foundBubblesByParticipantsName: Bubble[] = [];
        const bubbleSearchResults: BubbleSearchResult[] = [];

        // Check if some name" properties have been specified to restrict the search
        // If not, the search will be performed by default on the bubble name only
        if (options?.scope) {
            searchByBubbleName = options.scope.includes(BubbleSearchScope.SEARCH_BY_BUBBLE_NAME);
            searchByTagsName = options.scope.includes(BubbleSearchScope.SEARCH_BY_TAG_NAME);
            searchByParticipantsName = options.scope.includes(BubbleSearchScope.SEARCH_BY_PARTICIPANT_NAME);
            if (!searchByBubbleName && !searchByTagsName && !searchByParticipantsName) {
                searchByBubbleName = true;
                searchByParticipantsName = true;
            }
        } else {
            searchByBubbleName = true;
            searchByParticipantsName = true;
        }

        this.logger.debug(`[bubbleService] searchBubbles -- Pattern: '${anonymizer(searchPattern)}' -- byName: ${searchByBubbleName} - byTags: ${searchByTagsName} - byPart: ${searchByParticipantsName}`);

        // If requested, performs a search on the name of bubbles
        // Optimization: If a search on the bubble name is specified but not on the participants' names,
        // then the search will be performed locally without requesting the server.
        // if ((searchByBubbleName) && (!searchByParticipantsName)) {
        if (searchByBubbleName) {
            foundBubblesByName = this.searchRooms(searchPattern);
        }

        // If requested, performs a search on the name of participants
        if (searchByParticipantsName) {
            foundBubblesByParticipantsName = await this.getServerBubblesByParticipantsName(searchPattern);
        }

        // If requested, performs a search on the name of tags
        if (searchByTagsName && this.bubbleTagService) {
            foundBubblesByTag = await this.bubbleTagService.findBubblesByTag(searchPattern);
        }

        this.logger.debug(`[bubbleService] searchBubbles -- Nb results -- byName: ${foundBubblesByName.length} - byTags: ${foundBubblesByTag.length} - byPart: ${foundBubblesByParticipantsName.length}`);

        // Filter duplicates (Code could be better to perform filters among the 3 arrays .. Please just suggest ! !)
        // 1. Remove from foundBubblesByTag bubbles already found by bubble name
        // 2. Remove from foundBubblesByTag bubbles already found by participants names
        // 3. Remove from foundBubblesByParticipantsName bubbles already found by bubble name

        let filteredBubblesByTag: Bubble[] = foundBubblesByTag.filter((taggedBubble: Bubble) => {
            return foundBubblesByName.find((bubble: Bubble) => { return bubble && bubble.dbId === taggedBubble.dbId; }) === undefined;
        });
        filteredBubblesByTag = filteredBubblesByTag.filter((taggedBubble: Bubble) => {
            return foundBubblesByParticipantsName.find((bubble: Bubble) => { return bubble && bubble.dbId === taggedBubble.dbId; }) === undefined;
        });
        const filteredBubblesByParticipantsName: Bubble[] = foundBubblesByParticipantsName.filter((participantsBubble: Bubble) => {
            return foundBubblesByName.find((bubble: Bubble) => { return bubble && bubble.dbId === participantsBubble.dbId; }) === undefined;
        });

        foundBubbles = foundBubblesByName.concat(filteredBubblesByParticipantsName).concat(filteredBubblesByTag);

        // Sort the found bubbles acording to specified option or by using the default one.
        // For ease of use, the sorting is done BEFORE setting the data indicating how the bubbles were found.
        if (options?.sortBy) {
            if (options.sortBy === BubbleSearchSortBy.SORT_BY_BUBBLE_NAME) {
                foundBubbles.sort((bubble1: Bubble, bubble2: Bubble) => { return bubble1.name.localeCompare(bubble2.name); });
            } else if (options.sortBy === BubbleSearchSortBy.SORT_BY_LAST_ACTIVITY) {
                foundBubbles.sort((bubble1: Bubble, bubble2: Bubble) => { return bubble2.lastActivityDate.getTime() - bubble1.lastActivityDate.getTime(); });
            }
        } else {
            // By default, the found Bubbles are sorted according to displayName
            foundBubbles.sort((bubble1: Bubble, bubble2: Bubble) => { return bubble1.name.localeCompare(bubble2.name); });
        }

        foundBubbles.forEach((foundBubble: Bubble) => {
            const bubbleSearchResult: BubbleSearchResult = { bubble: null, foundBy: [] };
            bubbleSearchResult.bubble = foundBubble;

            this.logger.debug(`[bubbleService] searchBubbles -- foundBubble: name = ${anonymizer(foundBubble.name)} -- lastActivityDate = ${foundBubble.lastActivityDate}`);
            if (foundBubblesByName.find((bubble: Bubble) => { return bubble.dbId === foundBubble.dbId; })) {
                bubbleSearchResult.foundBy.push(BubbleSearchFoundBy.FOUND_BY_BUBBLE_NAME);
                // this.logger.info(`[bubbleService] searchBubbles -- foundBubble: name = ${foundBubble.name} -- FOUND_BY_BUBBLE_NAME`);
            }
            if (foundBubblesByTag.find((bubble: Bubble) => { return bubble.dbId === foundBubble.dbId; })) {
                bubbleSearchResult.foundBy.push(BubbleSearchFoundBy.FOUND_BY_TAG_NAME);
                // this.logger.info(`[bubbleService] searchBubbles -- foundBubble: name = ${foundBubble.name} -- FOUND_BY_TAG_NAME`);
            }
            if (foundBubblesByParticipantsName.find((bubble: Bubble) => { return bubble.dbId === foundBubble.dbId; })) {
                bubbleSearchResult.foundBy.push(BubbleSearchFoundBy.FOUND_BY_PARTICIPANT_NAME);
                // this.logger.info(`[bubbleService] searchBubbles -- foundBubble: name = ${foundBubble.name} -- FOUND_BY_PARTICIPANT_NAME`);
            }

            bubbleSearchResults.push(bubbleSearchResult);
        });

        return bubbleSearchResults;
    }

    /**
     * Accept invitation to join a bubble
     * @param bubble - the bubble to accept
     */
    public async acceptBubbleInvitation(bubble: Bubble): Promise<Bubble> {
        return this.updateMyUserInBubble(bubble as BubbleRB, BubbleUserStatus.ACCEPTED);
    }

    /**
     * Decline invitation to join a65a13c149cd2ae437afd46c4 bubble
     * @param bubble - the bubble to decline the invitation
     */
    public async declineBubbleInvitation(bubble: Bubble): Promise<Bubble> {
        return this.updateMyUserInBubble(bubble as BubbleRB, BubbleUserStatus.REJECTED);
    }

    /**
     *  User leaves the bubble. If createArchive is TRUE, the bubble will be archived, i.e. the user can only read the
     *  content that's already in the bubble, but no further action can be done inside the bubble
     *  The participant will go to UNSUBSCRIBED state (read-only)
     * @param bubble - The bubble to leave
     * @param createArchive - IF TRUE, the bubble will be archived for my user
     */
    public async leaveBubble(bubble: Bubble, createArchive: boolean = false): Promise<Bubble | undefined> {
        if (!bubble) { throw new Error("[bubbleService] leaveBubble -- bubble not found"); }
        if (createArchive) {
            await this.updateMyUserInBubble(bubble as BubbleRB, BubbleUserStatus.UNSUBSCRIBED);
            return bubble;
        }
        await this.userLeaveBubble(bubble as BubbleRB);
        return undefined;
    }

    /**
     * Delete own bubble. If createArchive is TRUE, the bubble will be archived, i.e. all users can only read the
     * content that's already in the bubble, but no further action can be done inside the bubble
     * All participants in the bubble will go to UNSUBSCRIBED state (read-only)
     * @param bubble - The bubble to delete
     * @param createArchive - IF TRUE, the bubble will be archived for all users (i.e. read only mode for all users still inside);
     */
    public async deleteMyBubble(bubble: Bubble, createArchive: boolean = false): Promise<Bubble | undefined> {
        if (!bubble) { throw new Error("[bubbleService] deleteMyBubble -- bubble not found"); }
        if (!bubble.isConnecteUserOwner()) { throw new Error("[bubbleService] deleteMyBubble -- user is not the owner of the bubble"); }

        try {
            if (createArchive) {
                await this.ownerArchiveRoom(bubble as BubbleRB);
                return bubble;
            }
            await this.ownerDeleteRoom(bubble as BubbleRB);
            this.sendEvent(BubbleServiceEvents.ON_BUBBLE_DELETED, { id: bubble?.dbId });
            this.logger.info(`[bubbleService] deleteMyBubble id: ${bubble?.dbId} -- SUCCESS`);
        }
        catch (error) {
            this.logger.error(`[bubbleService] Room deletion error ${bubble?.dbId} -- ${error?.message} ${error?.errorDetailsCode}`);
            throw error;
        }
        return undefined;
    }

    /**
    * Remove a user from the Bubble. API only available for moderators / owner of the bubble; The user will no longer have access to this bubble
    * nor the history;
    * @param bubble - the bubble
    * @param user - the contact that should be removed
    */
    public async removeUserFromBubble(bubble: Bubble, user: User): Promise<Bubble> {
        if (!bubble) { throw new Error("[bubbleService] removeUserFromBubble -- bubble not found"); }
        if (!bubble.isConnecteUserOwner() && !bubble.isConnecteUserOrganizer()) { throw new Error("[bubbleService] deleteMyBubble -- user is not the owner/moderator of the bubble"); }
        const userContact = user as Contact;
        try {
            this.logger.info("[bubbleService] removeUserFromBubble");

            const url = `${this.portalURL}rooms/${bubble.dbId}/users/${userContact.dbId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }

            this.logger.info(`[bubbleService] removeUserFromBubble ${userContact.dbId} from room ${bubble.dbId} (${bubble.getNameForLogs()}) success`);
            const user: BubbleUser = (bubble as BubbleRB).getUserByJid(userContact.jid);
            user.status = BubbleUserStatus.DELETED;
            return bubble;
        }
        catch (error) {
            if (error.data?.errorDetails) {
                const errorMessage = "removeUserFromBubble " + userContact.dbId + " from room " + bubble.dbId + "(" + bubble.getNameForLogs() + ") failure: " + error.data.errorDetails;
                this.logger.error("[bubbleService] " + errorMessage);
                throw new Error(errorMessage);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'removeUserFromBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
    * Update user's role in the Bubble. API only available for moderators / owner of the bubble; The user can be updated to "moderator" or "user";
    * @param bubble - The bubble
    * @param user - the user that should be update
    * @param role - "moderator" or "user"
    */
    public async updateUserRoleInBubble(bubble: Bubble, user: User, role: string) {
        if (!bubble) { throw new Error("[bubbleService] updateUserRoleInBubble -- bubble not found"); }
        if (!role) { throw new Error("[bubbleService] updateUserRoleInBubble -- role is missing"); }
        if (!bubble.isConnecteUserOwner() && !bubble.isConnecteUserOrganizer()) { throw new Error("[bubbleService] updateUserRoleInBubble -- user is not the owner/moderator of the bubble"); }
        const userContact = user as Contact;
        try {
            const data = { privilege: role };

            const url = `${this.portalURL}rooms/${bubble.dbId}/users/${userContact.dbId}`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw (response);

            this.logger.info(`[bubbleService] updateUserRoleInBubble ${userContact.nameForLogs} to room ${bubble.dbId} (${bubble.getNameForLogs()}) success`);
            const userData = (await response.json()).data;

            (bubble as BubbleRB).users.forEach((bubbleUser: BubbleUser) => {
                if (bubbleUser.user.jid === userData.jid_im) {
                    bubbleUser.role = userData.privilege;
                }
            });
            this.refreshMemberAndOrganizerLists(bubble as BubbleRB);
            return bubble;
        }
        catch (error) {
            if (error.data?.errorDetails) {
                const errorMessage = "updateUserRoleInBubble " + userContact.nameForLogs + " to room " + bubble.dbId + "(" + bubble.getNameForLogs() + ") failure: " + error.data.errorDetails;
                this.logger.error("[bubbleService] " + errorMessage);
                throw new Error(errorMessage);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateUserRoleInBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
    * Change the owner of the bubble
    * @param bubble - The bubble
    * @param newOwner - the user that should be the new owner
    */
    public async changeBubbleOwner(bubble: Bubble, newOwner: User): Promise<BubbleRB> {
        if (!bubble) { throw new Error("[bubbleService] changeBubbleOwner -- bubble not found"); }
        if (!bubble.isConnecteUserOwner()) { throw new Error("[bubbleService] changeBubbleOwner -- user is not the owner of the bubble"); }
        const newOwnerContact = newOwner as Contact;
        try {
            this.logger.info("[bubbleService] changeBubbleOwner");

            // Call the REST WebService
            const url = `${this.portalURL}rooms/${bubble.dbId}`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ owner: newOwnerContact.dbId });
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }

            bubble.owner = newOwnerContact;
            this.logger.info("[bubbleService] changeBubbleOwner successfully (" + bubble.dbId + ")");

            return this.bubbles[bubble.dbId];
        }
        catch (error) {
            if (error.data?.errorDetails) {
                const errorMessage = "changeBubbleOwner failure: " + error.data.errorDetails;
                this.logger.error("[bubbleService] " + errorMessage);
                throw new Error(errorMessage);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'changeBubbleOwner');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Update the bubble custom data
     * @param bubble - The bubble
     * @param customData - the custom data to be set to the bubble
     */
    public async updateBubbleCustomData(bubble: Bubble, customData: any): Promise<void> {
        try {
            this.logger.info("[bubbleService] updateBubbleCustomData");
            const url = `${this.portalURL}rooms/${bubble.dbId}/custom-data`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ customData: customData });
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }

            const data = (await response.json()).data;
            this.logger.info("[bubbleService] updateBubbleCustomData successfully got (" + JSON.stringify(data, null, 4) + ")");
            return data.customData || {};
        }
        catch (error) {
            if (error.data?.errorDetails) {
                const errorMessage = "updateBubbleCustomData failure: " + error.data.errorDetails;
                this.logger.error("[bubbleService] " + errorMessage);
                throw new Error(errorMessage);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateBubbleCustomData');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Update the bubble settings
     * @param bubble - The bubble
     * @param bubbleSettings - the data to be updated in the bubble
     */
    public async updateBubbleSettings(bubble: Bubble, bubbleSettings: BubbleSettings): Promise<Bubble> {
        try {
            const data = {};
            if (bubbleSettings.name) { data["name"] = bubbleSettings.name; }
            if (bubbleSettings.description !== null && bubbleSettings.description !== undefined) { data["topic"] = bubbleSettings.description; }
            if (bubbleSettings.muteUponEntry !== null && bubbleSettings.muteUponEntry !== undefined) { data["muteUponEntry"] = bubbleSettings.muteUponEntry; }
            if (bubbleSettings.playEntryTone !== null && bubbleSettings.playEntryTone !== undefined) { data["playEntryTone"] = bubbleSettings.playEntryTone; }
            if (bubbleSettings.includeAllPhoneNumbers !== null && bubbleSettings.includeAllPhoneNumbers !== undefined) { data["includeAllPhoneNumbers"] = bubbleSettings.includeAllPhoneNumbers; }
            if (bubbleSettings.autoAcceptInvitation !== null && bubbleSettings.autoAcceptInvitation !== undefined) { data["autoAcceptInvitation"] = bubbleSettings.autoAcceptInvitation; }
            if (bubbleSettings.autoRegister !== null && bubbleSettings.autoRegister !== undefined) { data["autoRegister"] = bubbleSettings.autoRegister; }
            if (bubbleSettings.contentToAutoClear !== null && bubbleSettings.contentToAutoClear !== undefined) { data["contentToAutoClear"] = bubbleSettings.contentToAutoClear; }
            if (bubbleSettings.recordingAndTranscription !== null && bubbleSettings.recordingAndTranscription !== undefined) { data["recordingAndTranscription"] = bubbleSettings.recordingAndTranscription; }

            const url = `${this.portalURL}rooms/${bubble.dbId}`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(data);

            this.logger.info(`[bubbleService] updateBubbleSettings ${bubble.dbId} with body ${body}`);

            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }

            const bubbleData = (await response.json()).data;

            this.bubbles[bubbleData.id].description = bubbleData.topic;

            // hack to update conference data from server
            if (bubbleData.conference) {
                this.bubbles[bubbleData.id].conference = bubbleData.conference ? bubbleData.conference : null;
            }
            this.logger.info("[bubbleService] updateBubbleSettings successfully (" + bubbleData.id + ", " + bubbleData.jid + ")");
            return this.bubbles[bubbleData.id];
        }
        catch (error) {
            if (error.data?.errorDetails) {
                const errorMessage = "updateBubbleSettings failure: " + error.data.errorDetails;
                this.logger.error("[bubbleService] " + errorMessage);
                throw new Error(errorMessage);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateBubbleSettings');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Get the bubble settings
     * @param bubble - The bubble
     */
    public getBubbleSettings(bubble: Bubble): BubbleSettings {
        const settings: BubbleSettings = {};
        const bubbleRB: BubbleRB = bubble as BubbleRB;

        settings.name = bubble.name;
        settings.description = bubble.description;
        settings.autoRegister = bubble.autoRegister ? bubble.autoRegister : "unlock";
        settings.autoAcceptInvitation = bubble.autoAcceptInvitation;
        settings.muteUponEntry = bubbleRB.conference ? bubbleRB.conference.muteUponEntry : "false";
        settings.playEntryTone = bubbleRB.conference ? bubbleRB.conference.playEntryTone : "true";
        settings.includeAllPhoneNumbers = bubbleRB.conference ? bubbleRB.conference.includeAllPhoneNumbers : false;
        settings.contentToAutoClear = bubbleRB.conference
            ? bubbleRB.conference.contentToAutoClear
            : {
                im: false,
                poll: false,
                document: false,
                member: false
            };

        return settings;
    }

    /**
     * clearBubbleData
     * Clear some data from bubble
     * @param bubble - The bubble
     * @param clearData - array containing the data to clear
     */
    public async clearBubbleData(bubble: Bubble, clearData: BubbleCleanupData[]): Promise<void> {
        try {
            const url = `${this.roomPortalURL}rooms/${bubble.dbId}/clearcontent`;
            const headers = this.authService.getPostHeader();
            const data = {
                contentToClear: {
                    im: false,
                    poll: false,
                    document: false,
                    member: false,
                    record: false
                }
            };
            clearData.forEach((value) => {
                data.contentToClear[value] = true;
            });
            const body = JSON.stringify(data);

            this.logger.info(`[bubbleService] clearBubbleData ${bubble.dbId} with body ${body}`);

            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) { throw (response); }

            const statusData = (await response.json()).data;
            bubble.contentToClear = data.contentToClear;
            this.logger.info("[bubbleService] clearBubbleData successfully (" + statusData + ")");
            return;
        }
        catch (error) {
            if (error.data?.errorDetails) {
                const errorMessage = "clearBubbleData failure: " + error.data.errorDetails;
                this.logger.error("[bubbleService] " + errorMessage);
                throw new Error(errorMessage);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'clearBubbleData');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }

    }

    // ------------------------------------------------------------
    // PRIVATE API FOR BUBBLE CREATION AND USER MANAGEMENT
    // ------------------------------------------------------------

    private async createBubbleInternal(bubbleName: string = "", createBubbleData: BubbleCreationData = {}) {
        // Add default name if there's none
        if (!bubbleName) {
            bubbleName = this.contactService.userContact.initials + " " + this.i18n.translate("new") + " " + this.i18n.translate("bubble") + " ";
        }
        const bubbleNameForLogs = bubbleName.charAt(0) + "***" + bubbleName.slice(-1);

        try {
            this.logger.info("[bubbleService] createRoom \"" + bubbleNameForLogs + "\"");

            let history: string = "all";
            if (createBubbleData.hasOwnProperty('history')) {
                history = createBubbleData.history ? "all" : "none";
            }
            const autoAccept = Boolean(createBubbleData.autoAcceptInvitation);
            const description = createBubbleData.description ? createBubbleData.description : "";
            let visibility: string = "private";
            if (createBubbleData.hasOwnProperty('visibility')) {
                visibility = createBubbleData.visibility === BubbleVisibility.PUBLIC ? "public" : "private";
            }

            const data = {
                name: bubbleName, topic: description, history, disableNotifications: true, createConversation: true,
                autoAcceptInvitation: autoAccept, muteUponEntry: Boolean(createBubbleData.muteUponEntry),
                playEntryTone: createBubbleData.playEntryTone, visibility
            };

            const url = `${this.portalURL}rooms/`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(data);

            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw (response);
            const bubbleData = (await response.json()).data;

            if (this.bubbles[bubbleData.id]) {
                this.logger.info("[bubbleService] createRoom -- room \"" + bubbleNameForLogs + "\" already exists");
                return this.bubbles[bubbleData.id];
            }

            const bubble = BubbleRB.create();
            bubble.update(bubbleData);
            bubble.owner = this.contactService.userContact;

            // Add bubble participants
            bubbleData.users.forEach((userData: any) => {
                const contact = this.contactService.getContactFromCacheById(userData.userId);
                if (contact) {
                    const newUserBubble: BubbleUser = { user: contact, status: userData.status, date: new Date(userData.additionDate), role: userData.privilege, invitingUserId: userData.invitingUserId };
                    bubble.users.push(newUserBubble);
                    // If The current bubble user is the connected user, mandatory to update bubbleConnectedUser !
                    if (this.contactService.isUserContact(contact)) {
                        bubble.bubbleConnectedUser = newUserBubble;
                    }
                }
            });
            this.refreshMemberAndOrganizerLists(bubble);
            bubble.usersReady.next(true);
            this.bubbles[bubble.dbId] = bubble;
            this.bubblesByJids[bubble.jid] = bubble;
            this.logger.info("[bubbleService] createRoom \"" + bubbleNameForLogs + "\" -- success");

            // Use try catch to avoid blocking avatar stuff
            try { await bubble.setCustomAvatar(createBubbleData.bubbleAvatar); }
            catch { this.logger.error("[bubbleService] createRoom: setBubbleAvatar ERROR"); }

            await this.sendInitialRoomPresenceSync(bubble);
            return this.bubbles[bubble.dbId];
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createRoom');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private async addUsersToBubble(bubble: BubbleRB, userList: User[] = [], emails: string[] = [], role?: BubbleUserRole) {
        try {
            if (!emails?.length && !userList?.length) return null;

            const users = userList.map((contact: Contact) => { return contact.dbId; });
            const bodyParams = { scenario: "chat", emails: emails, lang: this.getLanguageWithIsoCode(), users };

            if (role) { bodyParams["privilege"] = role; }

            this.logger.info("[bubbleService] addUsersToBubble");

            const url = `${this.portalURL}rooms/${bubble.dbId}/invitations`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(bodyParams);
            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) { throw (response); }

            const myBody = await response.json();
            const invalidUsers = [];

            //in case there's an error in the invitation of some users, remove them from the added users
            if (myBody.data?.failed?.invalidUsers.length) {
                if (users?.length) {
                    users.forEach((dbId, index) => {
                        if (myBody.data?.failed?.invalidUsers.some((invalidUser) => { return invalidUser?.id === dbId; })) {
                            users.splice(index, 1);
                        }
                    });
                }

                myBody.data?.failed?.invalidUsers.forEach((invalidUser) => {
                    const locError = this.errorHelperService.getLocalizedError(invalidUser.errorDetailsCode, null, null);
                    this.logger.warn(`[bubbleService] addUsersToBubble user not added ${invalidUser.id} -- ${locError})`);
                    invalidUsers.push(invalidUser.id);
                });
            }

            //add users to the bubble
            users?.forEach((dbId) => {
                this.logger.info(`[bubbleService] addUsersToBubble user added -- ${dbId}`);
            });

            this.refreshMemberAndOrganizerLists(bubble);
            bubble.updateAvatarInfo();
            return invalidUsers;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addUsersToBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);

            if (errorInfo?.error?.errorDetailsCode === 403621) {
                const profileError: any = new Error("addUsersToBubble failure - Not Allowed : Participants max limit has been reached: " + this.maxBubblesUsers);
                profileError.status = profileError.errorDetailsCode = "403";
                profileError.errorDetailsLabel = `MaximumNumberRoomUsersReach`;
                profileError.errorDetailsCode = `403620`;
                this.logger.error("[bubbleService] " + profileError.message);
                throw profileError;
            }

            throw errorInfo.error;
        }
    }

    // ------------------------------------------------------------
    //      COMMON FUNCTIONS FOR PRESENCE
    // ------------------------------------------------------------

    private sendInitialRoomPresence(bubble: BubbleRB, attempt: number = 0) {
        const attemptInfo = attempt ? " -- attempt " + attempt : "";
        this.logger.info("[bubbleService] sendInitialRoomPresence " + attemptInfo + " -- " + bubble.getNameForLogs() + " -- " + bubble.dbId);
        const presenceIq = $pres({ to: bubble.jid + "/" + this.xmppService.fullJid });
        presenceIq.c("x", { xmlns: "http://jabber.org/protocol/muc" }).c("history", { maxchars: "0" });
        this.xmppService.send(presenceIq);
    }

    public sendInitialBulkRoomPresence(): Promise<any> {
        this.logger.info('[bubbleService] sendInitialBulkRoomPresence');
        const presenceIq = $pres({ to: `muc.${this.authService.xmppDomain}/${this.xmppService.fullJid}`, from: this.xmppService.jid, xmlns: 'jabber:client' });
        const ext = presenceIq.c('x', { xmlns: 'http://jabber.org/protocol/muc' });
        ext.c('history', { maxchars: '0' }).up();
        ext.c('options', { acknowledge: 'false', subject: 'false', capability: 'false', webinar: 'true' });
        return this.xmppService.send(presenceIq);
    }

    public sendInitialRoomPresenceSync(bubble: BubbleRB, intervalDelay: number = 7500): Promise<any> {
        if (bubble.initPresPromise) { return bubble.initPresPromise; }
        if (bubble.ready) {
            this.logger.info(`[bubbleService] sendInitialRoomPresenceSync -- ${bubble.getNameForLogs()} -- ${bubble.dbId} -- bubble already activated`);
            if (this.roomPresenceWaitingList?.length) {
                this.roomPresenceWaitingList.forEach((waitingListRoom, index) => {
                    if (waitingListRoom.dbId === bubble.dbId) {
                        this.roomPresenceWaitingList.splice(index, 1);
                    }
                });
            }
            return Promise.resolve();
        }

        bubble.initPresPromise = new Promise((resolve, reject) => {
            bubble.initPresPromiseResolve = resolve;
            this.sendInitialRoomPresence(bubble, 0);

            // Retry mechanism
            const maxAttemptNumber = 3;
            let attemptNumber = 0;

            bubble.initPresInterval = interval(intervalDelay)
                .subscribe(() => {
                    if (attemptNumber < maxAttemptNumber) {
                        // up to <maxAttemptNumber> retries
                        attemptNumber += 1;
                        this.sendInitialRoomPresence(bubble, attemptNumber);
                    } else {
                        // if no response after <maxAttemptNumber> retries, we clean the presence promise in the bubble
                        // (to make it possible for further trials to re-establish presence state and chat history access)
                        this.logger.error("[bubbleService] sendInitialRoomPresenceSync : no response after " + attemptNumber + " retries => clean presence promise and interval for " + bubble.getNameForLogs() + " -- " + bubble.jid);
                        reject("[bubbleService] sendInitialRoomPresenceSync : no response");
                        bubble.initPresPromise = null;
                        if (bubble.initPresInterval) {
                            bubble.initPresInterval.unsubscribe();
                            bubble.initPresInterval = null;
                        }

                        //refresh the bubble from the local bubble cache
                        bubble = this.bubbles[bubble.dbId];
                        //should sent an update, so that the conversation is updated and we can sent chat messages inside
                        // this.eventService.publish(RoomServiceEvents.ROOM_UPDATE_EVENT, bubble);
                    }
                });
        });
        return bubble.initPresPromise;
    }

    private sendUnavailableRoomPresence(bubble: BubbleRB): void {
        const presenceIq = $pres({ to: bubble.jid + "/" + this.contactService.userContact.jid, type: "unavailable" });
        presenceIq.c("x", { xmlns: "http://jabber.org/protocol/muc" });
        this.xmppService.send(presenceIq);
    }

    // ------------------------------------------------------------
    //                      INTERNAL APIs
    // ------------------------------------------------------------

    /**
     * Performs a search on the name of participants of the bubble visible by the connected user
     * @param searchPattern - The search criteria
     */
    public async getServerBubblesByParticipantsName(searchPattern: string): Promise<Bubble[]> {
        try {
            const foundBubbles: Bubble[] = [];

            if (!searchPattern) throw new Error(`Error: no pattern to search`);
            const url: string = `${this.portalURL}rooms?userId=${this.userContact.dbId}&offset=0&participantName=${searchPattern}&limit=1000`;
            const headers: any = this.authService.getRequestHeader();

            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const bubblesParticipantNames: any = (await response.json()).data;

            this.logger.debug(`[bubbleService] getServerBubblesByParticipantsName -- SUCCESS -- `);
            bubblesParticipantNames.forEach((bubbleData: any) => {
                const bubble: Bubble = this.getBubbleFromCache(bubbleData.id);
                if (bubble)
                    foundBubbles.push(bubble);
            });
            return foundBubbles;
        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, 'getServerBubblesByParticipantsName');
            this.logger.error(`[bubbleService] getServerBubblesByParticipantsName failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Archive bubble. The bubble will be archived, i.e. all users can only read the
     * content that's already in the bubble, but no further action can be done inside the bubble
     * All participants in the bubble will go to UNSUBSCRIBED state (read-only)
     * @param bubble - The bubble where the user wants to end the conference
     */
    private async ownerArchiveRoom(bubble: BubbleRB) {
        this.logger.info("[bubbleService] ownerArchiveRoom");
        try {
            if (!bubble.isActive) { await this.sendInitialRoomPresenceSync(bubble); }
            const urltmp = `${this.portalURL}rooms/${bubble.dbId}/archive`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({});
            const response: Response = await fetch(urltmp, { method: 'PUT', headers, body });
            if (!response.ok) throw (response);
            const usersData = (await response.json()).data.users;
            bubble.users.forEach((bubbleUser: BubbleUser) => {
                const userIsPresent = usersData.find(item => bubbleUser.user.jid === item.jid_im);
                if (userIsPresent) {
                    bubbleUser.role = userIsPresent.privilege;
                    bubbleUser.status = userIsPresent.status;
                }
            });
            this.refreshMemberAndOrganizerLists(bubble);
            this.logger.info("[bubbleService] unsubscribe all users successfully");
            this.logger.info("[bubbleService] ownerArchiveRoom successfully");
        }
        catch (error) {
            this.logger.error(`[bubbleService] ownerArchiveRoom failure ${error.message}`);
            throw error;
        }
    }

    /**
     * Internal function - get the inviting user from the bubble
     * Checks bubble.users array and looks for connected user entry and finally finds its invitingUserId
     * @param bubble - The bubble
     */
    public getInvitingUser(bubble: BubbleRB): Contact {
        const connectedUserId = this.contactService.userContact.dbId;
        let invitingUserId = null;
        bubble.users.forEach((user) => {
            if (user.user.dbId === connectedUserId) {
                invitingUserId = user.invitingUserId;
            }
        });

        const invitingUser = invitingUserId ? this.contactService.getContactFromCacheById(invitingUserId) : bubble.owner;
        return invitingUser;
    }

    /***
    * OWNER ROOM DELETE
    * Used by SDK (public)
    * Warning when modifying this method
    */
    private async ownerDeleteRoom(bubble: BubbleRB) {
        const bubbleId = bubble.dbId;
        try {
            if (!bubbleId) {
                this.logger.error("[bubbleService] ownerDeleteRoom failure because the bubbleId is empty ...");
                return null;
            }
            if (!bubble.isActive && bubble.isUserStillBelongToBubble(this.contactService.userContact.jid)) { await this.sendInitialRoomPresenceSync(bubble); }
            // Call the REST WebService
            const url = `${this.portalURL}rooms/${bubbleId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }

            this.logger.info("[bubbleService] ownerDeleteRoom " + bubble.dbId + "(" + bubble.getNameForLogs() + ") success");
            return bubble;
        }
        catch (error) {
            let errorMessage = "ownerDeleteRoom (" + bubbleId + ") failure: unknown error occurred";
            if (error?.data) { errorMessage = "ownerDeleteRoom (" + bubbleId + ") failure: " + error.data.errorDetails; }
            if (error && error.status === 404) {
                this.logger.info("[bubbleService] ownerDeleteRoom " + bubble.dbId + "(" + bubble.getNameForLogs() + ") not found, deleting it from local storage");
                delete this.bubbles[bubble.dbId];
            }
            else {
                const errorData = await error.json();
                const errorDetailsCode = errorData.errorDetailsCode;
                if (errorDetailsCode === 409111) {
                    this.logger.info("[bubbleService] ownerDeleteRoom -- failed -- we should activate and delete it again -- " + bubble.dbId + "(" + bubble.getNameForLogs() + ")");
                    //we should activate the bubble before delete it
                    return this.activateAndDeleteBubble(bubble);
                }
            }

            this.logger.error("[bubbleService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    private async activateAndDeleteBubble(bubble: BubbleRB) {
        await this.sendInitialRoomPresenceSync(bubble);
        setTimeout(async () => {
            const bubbleId = bubble.dbId;
            try {
                if (!bubbleId) {
                    this.logger.error("[bubbleService] ownerDeleteRoom failure because the roomId is empty ...");
                    return;
                }
                // Call the REST WebService
                const url = `${this.portalURL}rooms/${bubbleId}`;
                const headers = this.authService.getRequestHeader();
                const response: Response = await fetch(url, { method: 'DELETE', headers });
                if (!response.ok) { throw (response); }

                this.logger.info("[bubbleService] ownerDeleteRoom " + bubble.dbId + "(" + bubble.getNameForLogs() + ") success");
                bubble.rxSubject.next(undefined);
            }
            catch (error) {
                const errorData = await error.json();
                const errorDetailsCode = errorData.errorDetailsCode;
                this.logger.error("[bubbleService] ownerDeleteRoom " + bubble.dbId + "(" + bubble.getNameForLogs() + ") -- delete failure -- " + errorDetailsCode);
            }
        }, 2000);
    }

    /** Check if a participant in bubble can be promoted to owner */
    public async checkPromoteOwner(bubble: BubbleRB, user: User) {
        const contact = user as Contact;
        try {
            const url = `${this.portalURL}rooms/${bubble.dbId}/owners/${contact.dbId}/check`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            this.logger.info("[bubbleService] checkPromoteOwner " + contact.nameForLogs + " to bubble " + bubble.dbId + "(" + bubble.getNameForLogs() + ") success");
        }
        catch (error) {
            const errorData = await error.json();
            this.logger.error("[bubbleService] Error checkPromoteOwner");
            const errorDetailsCode = errorData.errorDetailsCode;

            let bubbleParticipantCountErrorMsg = null;
            let bubblePromoteMemberErrorMsg = null;
            let webRtcConfAllowedErrorMsg = null;
            let bubbleChangeOwnershipErrorMsg = null;

            const rberror: any = { name: "RainbowError", message: '' };
            if (errorDetailsCode === 409002) {
                if (errorData.errorDetailsData) {
                    if (Array.isArray(errorData.errorDetailsData)) {
                        errorData.errorDetailsData.forEach((errorDetails: any) => {
                            if (errorDetails.feature === "BUBBLE_PARTICIPANT_COUNT") {
                                bubbleParticipantCountErrorMsg = this.i18n.translate("promoteToOwnerFailureParticipantCountReached", { name: contact.displayName, maxValue: errorDetails.maxValue });
                            } else if (errorDetails.feature === "WEBRTC_CONFERENCE_ALLOWED") {
                                webRtcConfAllowedErrorMsg = this.i18n.translate("promoteToOwnerFailureWebRtcConferenceAllowed", { name: contact.displayName });
                            } else if (errorDetails.feature === "BUBBLE_PROMOTE_MEMBER") {
                                bubblePromoteMemberErrorMsg = this.i18n.translate("promoteToOwnerFailureBubblePromoteMember", { name: contact.displayName });
                            } else if (errorDetails.feature === "BUBBLE_PSTN_DIALIN") {
                                bubbleChangeOwnershipErrorMsg = this.i18n.translate("changeOwnershipPSTNAccessFailureBubble");
                            }
                        });

                        if (bubbleParticipantCountErrorMsg) {
                            rberror.message = bubbleParticipantCountErrorMsg;
                            rberror.details = { promotePossible: false };
                        } else if (bubbleChangeOwnershipErrorMsg) {
                            rberror.message = this.i18n.translate("promoteOrganizerToOwnerMsg", { name: contact.displayName });
                            rberror.details = { promotePossible: true, secondMessage: bubbleChangeOwnershipErrorMsg, removeShareBubbleByPhone: true };
                        } else if (webRtcConfAllowedErrorMsg) {
                            rberror.message = this.i18n.translate("promoteOrganizerToOwnerMsg", { name: contact.displayName });
                            rberror.details = { promotePossible: true, secondMessage: webRtcConfAllowedErrorMsg };
                        } else if (bubblePromoteMemberErrorMsg) {
                            rberror.message = this.i18n.translate("promoteOrganizerToOwnerMsg", { name: contact.displayName });
                            rberror.details = { promotePossible: true, secondMessage: bubblePromoteMemberErrorMsg };
                        }
                    } else {
                        rberror.message = this.i18n.translate("promoteToOwnerFailureNotInRoom", { name: contact.displayName });
                        rberror.details = { promotePossible: false };
                    }
                }
            }

            if (!rberror.message) {
                rberror.message = this.i18n.translate("promoteToOwnerFailure", { name: contact.displayName });
                rberror.details = { promotePossible: false };
            }
            throw rberror;
        }
    }

    public async updateRoomPhoneNumbers(bubbleId: string, dialInPhoneNumbers: DialInPhoneNumber[] = []) {
        try {
            this.logger.info("[bubbleService] updateRoomPhoneNumbers");

            const url = `${this.portalURL}rooms/${bubbleId}`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ phoneNumbers: dialInPhoneNumbers });
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }

            const bubble = (await response.json()).data;

            // hack to update conference data from server
            if (bubble.conference?.phoneNumbers?.length) {
                this.bubbles[bubble.id].conference.phoneNumbers = bubble.conference.phoneNumbers ? bubble.conference.phoneNumbers : [];
            }
            this.logger.info("[bubbleService] updateRoomPhoneNumbers successfully (" + bubble.id + ", " + bubble.jid + ")");
            return this.bubbles[bubble.id];
        }
        catch (error) {
            if (error.data?.errorDetails) {
                const errorMessage = "updateRoomPhoneNumbers failure: " + error.data.errorDetails;
                this.logger.error("[bubbleService] " + errorMessage);
                throw new Error(errorMessage);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateRoomPhoneNumbers');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // ------------------------------------------------------------
    //                      PARTICIPANT FUNCTIONS
    // ------------------------------------------------------------

    // Update my status
    private async updateMyUserInBubble(bubble: BubbleRB, status: string) {
        try {
            let url = `${this.portalURL}rooms/${bubble.dbId}/users/${this.contactService.userContact.dbId}`;
            if (this.context === 'webinar') url += '?context=webinar';
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ status });
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }
            this.logger.info("[bubbleService] updateMyUserInBubble " + this.contactService.userContact.nameForLogs + " to room " + bubble.dbId + "(" + bubble.getNameForLogs() + ") success");
            return bubble;
        }
        catch (error) {
            if (error.data?.errorDetails) {
                const errorMessage = "updateMyUserInBubble " + this.contactService.userContact.nameForLogs + " to room " + bubble.dbId + "(" + bubble.getNameForLogs() + ") failure: " + error.data.errorDetails;
                this.logger.error("[bubbleService] " + errorMessage);
                throw new Error(errorMessage);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateMyUserInBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /***
    * DELETE MYSELF FROM A ROOM
    */
    private async userLeaveBubble(bubble: BubbleRB) {
        const userId = this.contactService.userContact.dbId;
        try {
            this.logger.info("[bubbleService] userLeaveBubble");
            const url = `${this.portalURL}rooms/${bubble.dbId}/users/${userId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }

            this.logger.info("[bubbleService] userLeaveBubble " + userId + " from room " + bubble.dbId + "(" + bubble.getNameForLogs() + ") success");
            delete this.bubbles[bubble.dbId];
            return bubble;
        }

        catch (error) {
            if (error.data?.errorDetails && error.status !== 409) {
                const errorMessage = "userLeaveBubble " + userId + " from room " + bubble.dbId + "(" + bubble.getNameForLogs() + ") failure: " + error.data.errorDetails;
                this.logger.error("[bubbleService] " + errorMessage);
                throw new Error(errorMessage);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'userLeaveBubble');

            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /*********************************************************/
    /** ROOM XMPP EVENT HANDLERS                            */
    /*********************************************************/

    private onBubbleErrorMessage(stanza: any): boolean {
        try {
            const stanzaElem = xmlParser(stanza);
            const textElement = stanzaElem.find("text");
            const text = (textElement?.length) ? textElement.text() : "";
            if (text === "Only occupants are allowed to send messages to the conference") {
                this.logger.info("[bubbleService] onBubbleErrorMessage error -- missing presence in the bubble, resend it");
                const fromJid = stanzaElem.attr("from");
                const bubble = this.getBubbleFromCache(fromJid) as BubbleRB;
                if (bubble) {
                    bubble.ready = false;
                    this.sendInitialRoomPresenceSync(bubble);
                    bubble.isActive = true;

                    //update the bubble from the server
                    this.getServerBubble(bubble.dbId);

                }
            }
            return true;
        }
        catch {
            return true;
        }
    }

    /*
        MANAGE INVITATION EVENT FOR BUBBLES
    */
    private onBubbleInvitationMessage(stanza: any): boolean {
        try {
            const stanzaElem = xmlParser(stanza);
            const xElem = stanzaElem.find("x");
            if (stanzaElem.find("event").length && xElem.length) {
                const eventName = stanzaElem.find("event").attr("name");
                //ignore if not invitation event
                if (!eventName || eventName !== "invitation") { return true; }
                const roomId = xElem.first().attr("roomid");

                //ignore if missing data
                if (!roomId) {
                    this.logger.info("[bubbleService] onBubbleInvitationMessage error -- missing roomId");
                    return true;
                }

                this.logger.info("[bubbleService] onBubbleInvitationMessage : room event message with roomId " + roomId + " and event name " + eventName);

                setTimeout(() => {
                    //we need to force refresh of the bubble from the server to be sure of our current status
                    this.getServerBubble(roomId)
                        .then((bubble: BubbleRB) => {
                            if (bubble.isWebinarBubble()) {
                                bubble.webinarId = stanzaElem.find("x[webinarid]").attr('webinarid');
                            }

                            if (this.settingsService.getSetting('autoAcceptRoomInvite') === "true") {
                                this.acceptBubbleInvitation(bubble);
                                return;
                            }

                            this.logger.info(`[bubbleService] onBubbleInvitationMessage -- get server room success for roomId ${bubble.dbId} and roomJid  ${bubble.jid} and name ${bubble.getNameForLogs()} and status ${bubble.bubbleConnectedUser.status}`);

                            if (bubble.bubbleConnectedUser.status === BubbleUserStatus.INVITED) {
                                this.sendEvent(BubbleServiceEvents.ON_USER_INVITATION_RECEIVED, { bubble: bubble as BubbleInvitation });
                            }
                            else if (bubble.bubbleConnectedUser.status === BubbleUserStatus.ACCEPTED && bubble.isBubbleConferenceStarted()) {
                                // If this is an invitation, and I'm already in the room (case of auto-accept)
                                // And there's an active conference, we should show ringing incoming popup

                                bubble.sendEvent(BubbleEvents.ON_CONFERENCE_START_EVENT);
                                this.webConferenceService?.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_STARTED_EVENT_IN_BUBBLE, { roomDbId: bubble.dbId });
                            }
                        })
                        .catch((error: Error) => {
                            this.logger.error("[bubbleService] onBubbleInvitationMessage failure :  impossible to get associated room : " + error.message);
                            return true;
                        });
                }, 2000);
            }
            return true;
        }
        catch (error) {
            this.logger.error("[bubbleService] onBubbleInvitationMessage error " + error);
            return true;
        }
    }

    /*
        MANAGE EVENTS FOR BUBBLES (Conference start/stop, voicemails, etc)
    */
    private async onBubbleEventMessage(stanza: any) {
        try {
            const stanzaElem: XMLElement = xmlParser(stanza);
            if (stanzaElem.find("event").length > 0) {
                this.logger.info("[bubbleService] onBubbleEventMessage : room event message");
                const fromJid = stanzaElem.attr("from");
                const roomBareJid = this.xmppService.getBareJidFromJid(fromJid);
                let userJid = stanzaElem.find("event").attr("jid");
                const eventName = stanzaElem.find("event").attr("name");
                const msgId = stanzaElem.attr("id");
                let extraParameters = null;
                const flagNoNotificationPopUp = (stanzaElem.find("deskphone").text() === "true");//RQ6169
                let roomName: string;
                stanzaElem.find("x").forEach((item) => {
                    if (item.attr("xmlns") === "jabber:x:bubble:conference") {
                        roomName = item.attr("name");
                    }
                });
                const gmwi = stanzaElem.find("gmwi");
                if (gmwi?.length) {
                    userJid = this.contactService.userContact.jid;
                }
                let ownerId = "";
                let eventOwnerContact;

                this.logger.info("[bubbleService] onBubbleEventMessage : room event message with parameters " + roomBareJid + " || " + userJid + " || " + eventName + " || " + msgId);

                if (roomBareJid && userJid && eventName && msgId) {
                    let bubble: BubbleRB = this.getBubbleFromCache(roomBareJid) as BubbleRB;
                    if (!bubble) bubble = await this.getBubble(roomBareJid) as BubbleRB;
                    // bubble.noNotificationPopUp = flagNoNotificationPopUp;  // the deskphone tag does not mean anymore that we must not display the room incoming popup

                    const user: BubbleUser = bubble.getUserByJid(userJid);

                    if (eventName === "voicemessage" && user && user.status !== BubbleUserStatus.DELETED && gmwi.length) {
                        const newMsgs = gmwi.find("message").attr("new");
                        const oldMsgs = gmwi.find("message").attr("old");
                        this.sendInnerEvent(BubbleServiceInnerEvents.BUBBLE_ADMIN_MESSAGE_EVENT, { roomJid: roomBareJid, userJid, eventName, msgId, extraParameters: { newMsgs, oldMsgs } });
                        return true;
                    }

                    if (eventName === "conferenceDelegate" && user && user.status !== BubbleUserStatus.DELETED) {
                        const userId = stanzaElem.find("event").attr("user-id");
                        if (userId) {
                            //update the room conf owner ID in that case
                            bubble.setWebConferenceOwnerId(userId);
                            this.webConferenceService?.onConferenceDelegated(bubble.dbId, userId);
                            return true;
                        }

                        this.logger.info(`[bubbleService] onBubbleEventMessage : ${eventName} -- No user to delegate the conf to, ending the conf `);
                        bubble.conference.sessions = [];
                        this.bubbles[bubble.dbId] = bubble;
                        this.bubblesByJids[bubble.jid] = bubble;
                        bubble.sendEvent(BubbleEvents.ON_CONFERENCE_STOP_EVENT, "autoDelegationNotPossible");
                        this.webConferenceService?.sendWebConferenceEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_ENDED, bubble);
                    }

                    // Filter out specific dialIn messgaes
                    if (eventName === "dialInEnable" || eventName === "dialInDisable" || eventName === "dialInReset") {
                        return true;
                    }

                    // Filter out clear content mesages after having updated local bubble data
                    if (eventName === "clearcontent") {

                        const scopesElem = stanzaElem.find('scope');
                        if (scopesElem.length) {
                            const contenttoclear = { im: false, document: false, poll: false, member: false, record: false };
                            scopesElem.forEach((scope) => {
                                contenttoclear[scope?.text()] = true;
                            });
                            bubble.contentToClear = contenttoclear;
                        }
                        return true;
                    }

                    if (eventName === "autoclearcontent") {
                        return true;
                    }

                    // Poll messages: get poll id (only when publish)
                    if (eventName === "pollPublish") {
                        const pollId = stanzaElem.find('poll-info').attr('id');
                        extraParameters = { pollId: pollId };
                    }

                    // No need to broadcast message for a user that has been deleted
                    if (user && user.status !== BubbleUserStatus.DELETED) {
                        this.sendInnerEvent(BubbleServiceInnerEvents.BUBBLE_ADMIN_MESSAGE_EVENT, { roomJid: roomBareJid, userJid, eventName, msgId, extraParameters });
                    }

                    if (bubble.isWebinarBubble()) { return true; }

                    this.logger.info(`[bubbleService] onBubbleEventMessage : ${eventName}`);
                    if (eventName === "conferenceAdd") {
                        //do not update the room if no right for webrtc audio
                        if (!this.contactService.userContact.webrtcAudioEnabled) { return true; }

                        //find the jid of the owner of the conference
                        stanzaElem.find("x").forEach((item) => {
                            if (item.attr("xmlns") === "jabber:x:bubble:conference:owner") {
                                ownerId = item.attr("jid");
                            }
                        });
                        if (!ownerId) { ownerId = userJid; }
                        eventOwnerContact = this.contactService.getContactFromCacheByJid(ownerId);
                        if (!eventOwnerContact?.dbId) { await this.contactService.getContactsByJids([ownerId]); }
                        eventOwnerContact = this.contactService.getContactFromCacheByJid(ownerId);
                        bubble.addConferenceSession(eventOwnerContact.dbId);
                        this.bubbles[bubble.dbId] = bubble;
                        this.bubblesByJids[bubble.jid] = bubble;
                        bubble.sendEvent(BubbleEvents.ON_CONFERENCE_START_EVENT);
                        if (flagNoNotificationPopUp) { // deskphone mode
                            this.eventService.publish("ON_WEBCONFERENCE_STARTED_DESKPHONE_INVITATION", roomBareJid, roomName);
                        }

                        //send event only if the someone else has started the conference
                        if (!this.contactService.isUserContact(eventOwnerContact)) {
                            this.webConferenceService?.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_STARTED_EVENT_IN_BUBBLE, { roomDbId: bubble.dbId });
                        }
                        if (this.webConferenceService) {
                            if (!this.webConferenceService?.webConferenceSessions[bubble.dbId]) {
                                const webConference = new WebConferenceSession(bubble.dbId, bubble);
                                this.webConferenceService.webConferenceSessions[bubble.dbId] = webConference;
                            }

                            //do not update our status if the user is in progress to join
                            if (this.webConferenceService.webConferenceSessions[bubble.dbId].status === BubbleConferenceStatus.ENDED) {
                                this.webConferenceService.webConferenceSessions[bubble.dbId].status = BubbleConferenceStatus.UNJOINED;
                            }

                            this.webConferenceService?.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STARTED, { bubbleConference: this.webConferenceService.webConferenceSessions[bubble.dbId] });
                        }
                    }

                    if (eventName === "conferenceRemove") {
                        bubble.conference.sessions = [];
                        this.bubbles[bubble.dbId] = bubble;
                        this.bubblesByJids[bubble.jid] = bubble;
                        bubble.sendEvent(BubbleEvents.ON_CONFERENCE_STOP_EVENT);

                        //to be reworked when reworking web conf service
                        this.webConferenceService?.sendWebConferenceEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_ENDED, bubble);

                    }
                }
            }
            return true;
        } catch (error) {
            this.logger.error("[bubbleService] onBubbleEventMessage error " + error);
            return true;
        }
    }

    private onBubbleHistoryReadyMessage(stanza: any) {
        const stanzaElem = xmlParser(stanza);
        const changedElem = stanzaElem.find("changed");
        if (changedElem.length > 0) {
            const roomId = changedElem.attr("with");
            const room = this.getBubbleFromCache(roomId);
            this.logger.debug("[bubbleService] onBubbleHistoryReadyMessage with " + roomId);
            this.sendInnerEvent(BubbleServiceInnerEvents.BUBBLE_HISTORY_READY, { room });
        }
        return true;
    }

    /*
        MANAGE OPEN INVITE UPDATES FOR BUBBLES
    */
    private onRoomOpeninviteUpdates(openinviteElem) {
        try {
            // Handle "room openinvite" message
            if (openinviteElem.length > 0) {
                this.logger.info("[bubbleService] onRoomOpeninviteUpdates -- openinvite");
                const openInviteAction = openinviteElem.attr("action");
                const roomId = openinviteElem.find("roomid").text();
                const openInviteId = openinviteElem.find("openinviteid").text();
                const roomType = openinviteElem.find("roomType").text();
                const invitationURL = openinviteElem.find("invitationURL").text();

                switch (openInviteAction) {
                    case "update":
                        // Cleanup previous roomId / openInviteId association if any
                        Object.keys(this.openInviteIdByRoomId).forEach((key) => {
                            if (this.openInviteIdByRoomId[key]
                                && this.openInviteIdByRoomId[key].openInviteId !== openInviteId
                                && key === roomId) {
                                this.logger.info("[bubbleService] onRoomOpeninviteUpdates -- openinviteId updated for " + key);
                                this.openInviteIdByRoomId[key].openInviteId = openInviteId;
                                this.openInviteIdByRoomId[key].openInviteLink = invitationURL;
                                this.openInviteIdByRoomIdSubject.next({
                                    invitationURL: invitationURL,
                                    openInviteId: openInviteId,
                                    roomType: roomType,
                                    id: roomId,
                                    action: `updated`
                                });
                            }
                        });
                        break;

                    case "create":
                        if (roomId && openInviteId) {
                            this.openInviteIdByRoomId[roomId] = {};
                            this.openInviteIdByRoomId[roomId].roomType = roomType;
                            this.openInviteIdByRoomId[roomId].openInviteId = openInviteId;
                            this.openInviteIdByRoomId[roomId].openInviteLink = invitationURL;
                            this.openInviteIdByRoomIdSubject.next({
                                invitationURL: invitationURL,
                                openInviteId: openInviteId,
                                roomType: roomType,
                                id: roomId,
                                action: `updated`
                            });
                            this.logger.info("[bubbleService] onRoomOpeninviteUpdates -- openinviteId " + openInviteId + " assigned to room " + roomId);
                            this.eventService.publish("ON_OPEN_INVITE_ID_UPDATED_EVENT", this.bubbles[roomId]);

                        }
                        break;

                    case "delete":
                        // Cleanup previous roomId / openInviteId association if any
                        Object.keys(this.openInviteIdByRoomId).forEach((key) => {
                            if (this.openInviteIdByRoomId[key]
                                && this.openInviteIdByRoomId[key].openInviteId === openInviteId
                                && key === roomId) {
                                this.logger.info("[bubbleService] onRoomOpeninviteUpdates -- openinviteId removed from " + key);
                                delete this.openInviteIdByRoomId[key];
                                this.openInviteIdByRoomIdSubject.next({
                                    invitationURL: invitationURL,
                                    openInviteId: openInviteId,
                                    roomType: roomType,
                                    id: roomId,
                                    action: `deleted`
                                });
                                this.eventService.publish("ON_OPEN_INVITE_ID_DELETED_EVENT", this.bubbles[roomId]);
                            }
                        });
                        break;
                    default:
                        this.logger.info("[bubbleService] onRoomOpeninviteUpdates -- openinviteId - unkown action : " + openInviteAction);
                        break;
                }
            }
        }
        catch (error) {
            this.logger.error(`[bubbleService] onRoomOpeninviteUpdates -- openinviteId - error : ${error}`);
        }
    }

    private async manageonBubbleUpdateConfigMessage(bubble: BubbleRB, bubbleElem: any): Promise<void> {

        try {
            const logMessage = `[bubbleService] manageonBubbleUpdateConfigMessage -- management -- ${bubble.dbId} --`;
            let serverUpdateMandatory = false;
            // Handle room name and description
            const description = bubbleElem.attr('topic');
            const name = bubbleElem.attr('name');
            const userJid = bubbleElem.attr("userjid");
            const userStatus = bubbleElem.attr("status");
            const privilege = bubbleElem.attr("privilege");
            const vcard = bubbleElem.attr("vcard");
            const reload = bubbleElem.attr("reload");

            if (reload === 'true') {
                serverUpdateMandatory = true;
            }

            const recordingAndTranscriptionElem = bubbleElem.find("recordingAndTranscription");
            if (recordingAndTranscriptionElem?.length > 0) {
                const recordingAndTranscription: ConferenceRecordingConfig = {
                };
                /* if (recordingAndTranscriptionElem.attr('recordingName'))
                    recordingAndTranscription.recordingName = recordingAndTranscriptionElem.attr('recordingName'); */
                if (recordingAndTranscriptionElem.attr('persistent'))
                    recordingAndTranscription.persistent = recordingAndTranscriptionElem.attr('persistent') === 'true';
                if (recordingAndTranscriptionElem.attr('transcription'))
                    recordingAndTranscription.transcription = recordingAndTranscriptionElem.attr('transcription') === 'true';
                if (recordingAndTranscriptionElem.attr('summary'))
                    recordingAndTranscription.summary = recordingAndTranscriptionElem.attr('summary') === 'true';
                if (recordingAndTranscriptionElem.attr('summaryForm'))
                    recordingAndTranscription.summaryForm = recordingAndTranscriptionElem.attr('summaryForm');
                if (recordingAndTranscriptionElem.attr('summaryPrompt'))
                    recordingAndTranscription.summaryPrompt = recordingAndTranscriptionElem.attr('summaryPrompt');
                if (recordingAndTranscriptionElem.attr('inputLanguageLiveTranscription'))
                    recordingAndTranscription.inputLanguageLiveTranscription = recordingAndTranscriptionElem.attr('inputLanguageLiveTranscription');
                if (recordingAndTranscriptionElem.attr('inputLanguageTranscription'))
                    recordingAndTranscription.inputLanguageTranscription = recordingAndTranscriptionElem.attr('inputLanguageTranscription');
                bubble.setConferencePreConfig(recordingAndTranscription);
            }

            const playEntryToneElem = bubbleElem.attr('playEntryTone');
            if (playEntryToneElem) {
                this.logger.info(`${logMessage} update playEntryTone`);
                bubble.setPlayEntryTone((playEntryToneElem === 'true'));
            }

            const muteUponEntryElem = bubbleElem.attr('muteUponEntry');
            if (muteUponEntryElem) {
                this.logger.info(`${logMessage} update muteUponEntry`);
                bubble.setMuteUponEntry((muteUponEntryElem === 'true'));
            }

            const includeAllPhoneNumbersElem = bubbleElem.attr('includeAllPhoneNumbers');
            if (includeAllPhoneNumbersElem) {
                this.logger.info(`${logMessage} update includeAllPhoneNumbers`);
                bubble.setIncludeAllPhoneNumbers((includeAllPhoneNumbersElem === 'true'));
            }

            // When phone numbers for PSTN Dial In are updated on room
            const phoneNumbersUpdatedElem = bubbleElem.attr('phoneNumbersUpdated');
            if (phoneNumbersUpdatedElem === 'true') {
                serverUpdateMandatory = true;
            }

            if (name || description) {
                this.logger.info(`${logMessage} update name or description`);
                bubble.name = name;
                bubble.description = description ? description : '';
            }

            // handle bubble autoRegister
            const autoRegister = bubbleElem.attr('autoRegister');
            if (autoRegister) {
                this.logger.info(`${logMessage} update autoRegister: ${autoRegister}`);
                bubble.setAutoRegister(autoRegister);
            }

            // Handle bubble contentToAutoclear (bubble cleanup)
            const contentToAutoclearElem = bubbleElem.find('contentToAutoclear');
            if (contentToAutoclearElem.length) {
                const imValue = Boolean(contentToAutoclearElem.attr('im'));
                const documentValue = Boolean(contentToAutoclearElem.attr('document'));
                const memberValue = Boolean(contentToAutoclearElem.attr('member'));
                const pollValue = Boolean(contentToAutoclearElem.attr('poll'));
                this.logger.info(`${logMessage} update contentToAutoclear ${imValue} ${documentValue} ${memberValue} ${pollValue}`);
                bubble.setContentToAutoclear({ im: imValue, document: documentValue, member: memberValue, poll: pollValue });
            }

            // Handle custom datajabber:iq:configuration
            const customData = bubbleElem.attr('customData');
            if (customData) {
                try {
                    const extractedContent = this.extractHtmlContent(customData);
                    bubble.customData = JSON.parse(extractedContent);
                    this.logger.info(`${logMessage} handle customData`);
                }
                catch (err) {
                    this.logger.error(`${logMessage} handle customData -- FAILURE -- ${err.message}`);
                    bubble.customData = '{}';
                }
            }

            // Handle alerts (is enabled or disabled)
            const isAlertNotificationEnabled: string = bubbleElem.attr("alertenabled");
            if (isAlertNotificationEnabled) {
                bubble.isAlertNotificationEnabled = (isAlertNotificationEnabled === 'true'); // Handle string to boolean scenario
            }

            let bubbleUser: BubbleUser = bubble.getUserByJid(userJid);
            if (userStatus) {
                let newBubbleUser = false;
                if (!bubbleUser) {
                    // This is webinar bubble, add user from data
                    if ((this.bubbleServiceConfig?.webinar || bubble.isWebinarBubble()) && userStatus === 'accepted') {
                        let contact: Contact = null;
                        let roomData: any = null;
                        bubbleElem.find('payload').forEach((payloadElem: XMLElement) => {

                            const datatype = payloadElem.attr('datatype');
                            const jsonElem = payloadElem.find('json');
                            if (datatype === 'urn:rainbow:json:userdata') contact = this.contactService.createContactFromData(JSON.parse(jsonElem.first().text()));
                            if (datatype === 'urn:rainbow:json:roomuserdata') roomData = JSON.parse(jsonElem.first().text());
                        });
                        const newUserBubble: BubbleUser = { user: contact, status: BubbleUserStatus.ACCEPTED, date: new Date(roomData.additionDate), role: roomData.privilege };
                        bubble.users.push(newUserBubble);
                        // If The current bubble user is the connected user, mandatory to update bubbleConnectedUser !
                        if (this.contactService.isUserContact(contact)) {
                            bubble.bubbleConnectedUser = newUserBubble;
                            if (!bubble.isConnecteUserOwner())
                                this.sendEvent(BubbleServiceEvents.ON_USER_INVITATION_ACCEPTED, { bubble: bubble, bubbleUser: bubble.bubbleConnectedUser });
                        }
                        this.refreshMemberAndOrganizerLists(bubble);
                        bubble.updateAvatarInfo();
                    }
                    else {
                        // User is not in the room, we need to add him manually

                        try {
                            this.logger.info(`${logMessage} User not in room add it`);

                            const contact = await this.contactService.getContactByJid(userJid, true);
                            if (!contact) serverUpdateMandatory = true;
                            else {
                                // Add user to room in case it's not already done
                                if (!bubble.getUserByJid(userJid)) {
                                    this.logger.info(`${logMessage} add user ${contact.dbId} with role ${privilege}`);
                                    const newUserBubble: BubbleUser = { user: contact, status: userStatus, date: new Date(), role: privilege ? privilege : "user" };
                                    bubble.users.push(newUserBubble);
                                    // If The current bubble user is the connected user, mandatory to update bubbleConnectedUser !
                                    if (this.contactService.isUserContact(contact)) {
                                        bubble.bubbleConnectedUser = newUserBubble;
                                    }
                                    // Manage the bubble guest case, userStatus === "accepted" (no invitation step)
                                    // In this particular case update the counter
                                    if (userStatus === 'accepted') bubble.nbParticipants += 1;
                                    newBubbleUser = true;
                                }
                            }
                        }
                        catch (error) {
                            this.logger.info(`${logMessage} error ${error}`);
                            serverUpdateMandatory = true;
                        }
                    }
                }

                if (!serverUpdateMandatory) {
                    // Do the update
                    bubbleUser = bubble.getUserByJid(userJid);
                    if (bubbleUser.status !== userStatus || newBubbleUser) {
                        this.updateUserStatus(bubble, bubbleUser, userStatus);
                    }
                    this.refreshMemberAndOrganizerLists(bubble);
                    bubble.updateAvatarInfo();
                }
            }

            if (privilege && !serverUpdateMandatory) {
                bubbleUser = bubble.getUserByJid(userJid);

                if (this.bubbleServiceConfig?.ignoreRole && (this.bubbleServiceConfig?.webinar || bubble.isWebinarBubble())) {
                    // do nothing for webinar
                }
                else if (bubbleUser.role !== privilege) {
                    this.logger.info(`${logMessage} update user's ${bubbleUser.user.dbId} role to ${privilege}`);
                    //find the user and update his role
                    if (privilege === "user") { bubbleUser.role = BubbleUserRole.USER; }
                    if (privilege === "moderator") { bubbleUser.role = BubbleUserRole.ORGANIZER; }
                    if (privilege === "owner") { bubble.owner = bubbleUser.user as Contact; }

                    // If the update is on my user
                    if (bubbleUser.user.jid === this.contactService.userContact.jid) {
                        if (privilege === "owner") {
                            // If I'm the new owner, show a notification
                            const message = this.i18n.translate("promotedToOwner", { name: bubble.name });
                            this.eventService.publish("GLOBAL_NOTIFY_MESSAGE_EVENT", message);
                        }
                        this.sendEvent(BubbleServiceEvents.ON_CONNECTED_USER_ROLE_UPDATED, bubble);
                    }
                    bubble.rxSubject.next(RBEvent.create(BubbleEvents.ON_USER_ROLE_UPDATE, bubbleUser));
                    this.refreshMemberAndOrganizerLists(bubble);
                }
            }

            if (vcard === "updated" && !serverUpdateMandatory) {
                bubbleUser = bubble.getUserByJid(userJid);
                if (!bubbleUser && !bubble.usersReady?.value) {
                    //get the bubble from server
                    await this.updateRoomFromServer(bubble);
                    bubbleUser = bubble.getUserByJid(userJid);
                }

                const lastname = bubbleElem.find("lastname").text();
                const firstname = bubbleElem.find("firstname").text();
                const companyNameOfGuest = bubbleElem.find("companyNameOfGuest").text();
                if (companyNameOfGuest && bubbleUser.user.guest) (bubbleUser.user as Contact).companyNameOfGuest = companyNameOfGuest;
                if (lastname || firstname) { (bubbleUser.user as Contact).updateName(firstname, lastname); }
            }

            if (serverUpdateMandatory) await this.updateRoomFromServer(bubble);
            else bubble.sendEvent(BubbleEvents.ON_DATA_UPDATED);

        }
        catch (error) {
            this.logger.error(`[bubbleService] manageonBubbleUpdateConfigMessage - error : ${error}`);
        }
    }

    /*
        MANAGE CONFIG UPDATE EVENTS FOR A BUBBLE (name, description, avatar, participants, etc)
    */
    private async onBubbleUpdateConfigMessage(stanza: any) {
        try {
            const stanzaElem = xmlParser(stanza);
            const roomElem = stanzaElem.find("room");
            const roomLobby = stanzaElem.find("roomlobby");
            const roomLobbyAccess = stanzaElem.find("lobbyaccess");

            // Handle "Offline Storage" message
            const delayElem = stanzaElem.find('delay');
            if (delayElem.length > 0 && delayElem.text() === 'Offline Storage') { return true; }

            //manage openinvite updates
            const openinviteElem = stanzaElem.find("openinvite");
            if (openinviteElem.length > 0) {
                this.onRoomOpeninviteUpdates(openinviteElem);
            }

            if (roomLobby.length) {
                const roomId = roomLobby.attr('roomid');
                const logMessage = `[bubbleService] onBubbleUpdateConfigMessage -- lobby/waiting room management -- ${roomId} --`;
                let bubble: BubbleRB = this.getBubbleFromCache(roomId) as BubbleRB;

                //if bubble is not there, fetch it from server
                if (!bubble) { bubble = await this.getBubble(roomId) as BubbleRB; }
                if (bubble) {
                    const action = (roomLobby.attr('action')) ? roomLobby.attr('action') : undefined;
                    const waitingusers = roomLobby.attr('waitingusers');
                    const hasLobby = roomLobby.attr('haslobby');

                    const accessDeniedLobbyRoom = roomElem.attr('accessdenied');
                    if (accessDeniedLobbyRoom) {
                        if (!this.connectedUserPendingRequests[bubble.dbId]) {
                            //manually add pending request, in case of guest
                            this.connectedUserPendingRequests[bubble.dbId] = { status: AccessRequestStatus.DENIED, additionDate: new Date().toDateString() };
                        }

                        this.connectedUserPendingRequests[bubble.dbId].status = AccessRequestStatus.DENIED;
                        bubble.sendEvent(BubbleEvents.ON_MY_USER_IN_LOBBY_UPDATE, { status: this.connectedUserPendingRequests[bubble.dbId].status });
                        this.sendEvent(BubbleServiceEvents.ON_MY_USER_REQUESTS_TO_JOIN_BUBBLE_CHANGE);
                    }

                    //manage change of the has lobby property for the bubble
                    if (hasLobby === "true" || action === "create") { if (!bubble.hasLobby) { bubble.hasLobby = true; this.logger.info(`${logMessage} is now ${bubble.hasLobby}`); } }
                    if (hasLobby === "false" || action === "delete") { if (bubble.hasLobby) { bubble.hasLobby = false; this.logger.info(`${logMessage} is now ${bubble.hasLobby}`); } }

                    const contactsToAdd = [];
                    //update the list of users in pending state for bubble
                    roomLobby.find("user").forEach((user) => {
                        const userId = user.attr("userid");
                        const status = user.attr("status");
                        const firstname = user.attr("firstname");
                        const lastname = user.attr("lastname");

                        //check if user is myself
                        if (userId === this.contactService.userContact.dbId) {
                            //my user status has been changed
                            if (status === "pending") {
                                const additionDate = user.attr("additionDate");
                                this.connectedUserPendingRequests[bubble.dbId] = { status: AccessRequestStatus.PENDING, additionDate: additionDate };
                            }

                            bubble.sendEvent(BubbleEvents.ON_MY_USER_IN_LOBBY_UPDATE, { status: this.connectedUserPendingRequests[bubble.dbId].status });

                            this.sendEvent(BubbleServiceEvents.ON_MY_USER_REQUESTS_TO_JOIN_BUBBLE_CHANGE);
                        }
                        else {
                            if (status === "pending") {
                                const additionDate = user.attr("additionDate");
                                const lobbyUser = this.lobbyUsersPerBubble[bubble.dbId]?.find((lobbyContact) => { return lobbyContact?.contact?.dbId === userId; });
                                if (!lobbyUser) {
                                    //check if user is anonymous guest, in that case do not add him, but wait for the update of name / lastname
                                    if (firstname || lastname) {
                                        //a new user is added to the lobby
                                        //contact to add in lobby for bubble
                                        contactsToAdd.push({ dbId: userId, additionDate: additionDate });
                                    }
                                }
                            }
                            else {
                                this.lobbyUsersPerBubble[bubble.dbId]?.forEach((lobbyContact, index) => {
                                    if (lobbyContact?.contact?.dbId === userId) {
                                        this.lobbyUsersPerBubble[bubble.dbId].splice(index, 1);
                                    }
                                });

                                bubble.sendEvent(BubbleEvents.ON_USERS_IN_LOBBY_UPDATE, { lobbyUsers: this.lobbyUsersPerBubble[bubble.dbId] });
                            }
                        }
                    });

                    //lobby is disabled, remove all pending requests
                    if (!bubble.hasLobby && this.lobbyUsersPerBubble[bubble.dbId]?.length) {
                        this.lobbyUsersPerBubble[bubble.dbId] = [];
                        bubble.sendEvent(BubbleEvents.ON_USERS_IN_LOBBY_UPDATE, { lobbyUsers: this.lobbyUsersPerBubble[bubble.dbId] });
                    }

                    if (contactsToAdd.length) {
                        const results = await this.addUsersToLobbyForBubble(bubble.dbId, contactsToAdd);

                        //send event for each added user to the lobby, so that we can generate the notification
                        results.forEach((lobbyUser) => {
                            this.sendEvent(BubbleServiceEvents.ON_REQUEST_TO_JOIN_BUBBLE_RECEIVED, { bubble, lobbyUser });
                        });
                    }

                    this.sendEvent(BubbleServiceEvents.ON_BUBBLES_LOBBY_LIST_CHANGE);
                }

                return;
            }

            if (roomLobbyAccess.length) {
                const roomId = roomLobbyAccess.attr('roomid');
                const logMessage = `[bubbleService] onBubbleUpdateConfigMessage -- management -- ${roomId} --`;
                const accessDeniedLobbyRoom = roomLobbyAccess.attr('accessdenied');
                const accessGrantedLobbyRoom = roomLobbyAccess.attr('accessgranted');

                if (accessDeniedLobbyRoom === 'true' || accessGrantedLobbyRoom === 'true') {
                    const bubble: BubbleRB = this.getBubbleFromCache(roomId) as BubbleRB;
                    const newStatus = (accessDeniedLobbyRoom === 'true') ? AccessRequestStatus.DENIED : AccessRequestStatus.ACCEPTED;

                    this.logger.info(`${logMessage} access ${newStatus} for lobby bubble`);
                    if (!this.connectedUserPendingRequests[roomId]) {
                        //manually add pending request, in case of guest
                        this.connectedUserPendingRequests[roomId] = { status: newStatus, additionDate: new Date().toDateString() };
                    }

                    this.connectedUserPendingRequests[roomId].status = newStatus;
                    this.connectedUserPendingRequests[roomId].ackRead = false;

                    bubble?.sendEvent(BubbleEvents.ON_MY_USER_IN_LOBBY_UPDATE, { status: this.connectedUserPendingRequests[roomId].status });

                    this.sendEvent(BubbleServiceEvents.ON_MY_USER_REQUESTS_TO_JOIN_BUBBLE_CHANGE);

                    if (newStatus === AccessRequestStatus.ACCEPTED) {
                        //check that the bubble is in correct state after being accepted
                        setTimeout(() => {
                            //reload bubble no matter what
                            const bubble: BubbleRB = this.getBubbleFromCache(roomId) as BubbleRB;
                            if (!bubble?.bubbleConnectedUser) {
                                //refresh the bubble from the server
                                this.getServerBubble(roomId);
                            } else {
                                //check for user status, and update if necessary
                                bubble.bubbleConnectedUser.status = BubbleUserStatus.ACCEPTED;
                                this.updateMyUserToAcceptedStatus(bubble, true);
                            }
                        }, 500);
                    }
                    return true;
                }
            }

            // Handle "room attribute management" message
            if (roomElem.length) {
                // Get room object
                const roomId = roomElem.attr('roomid');
                const logMessage = `[bubbleService] onBubbleUpdateConfigMessage -- management -- ${roomId} --`;
                const bubble: BubbleRB = this.getBubbleFromCache(roomId) as BubbleRB;

                // manage LOBBY EVENTS HERE, independant of bubble

                //sometimes we wont have the bubble, so we need to update the waiting list
                const accessDeniedLobbyRoom = roomElem.attr('accessdenied');
                const accessGrantedLobbyRoom = roomElem.attr('accessgranted');
                const acknotification = roomElem.attr('acknotification');
                const hidenotification = roomElem.attr('hidenotification');

                //will be removed from here
                if (accessDeniedLobbyRoom === 'true' || accessGrantedLobbyRoom === 'true') {
                    const newStatus = (accessDeniedLobbyRoom === 'true') ? AccessRequestStatus.DENIED : AccessRequestStatus.ACCEPTED;

                    this.logger.info(`${logMessage} access ${newStatus} for lobby bubble`);
                    if (!this.connectedUserPendingRequests[roomId]) {
                        //manually add pending request, in case of guest
                        this.connectedUserPendingRequests[roomId] = { status: newStatus, additionDate: new Date().toDateString() };
                    }

                    this.connectedUserPendingRequests[roomId].status = newStatus;
                    this.connectedUserPendingRequests[roomId].ackRead = false;

                    bubble?.sendEvent(BubbleEvents.ON_MY_USER_IN_LOBBY_UPDATE, { status: this.connectedUserPendingRequests[roomId].status });

                    this.sendEvent(BubbleServiceEvents.ON_MY_USER_REQUESTS_TO_JOIN_BUBBLE_CHANGE);

                    if (newStatus === AccessRequestStatus.ACCEPTED) {
                        //check that the bubble is in correct state after being accepted
                        setTimeout(() => {
                            //reload bubble no matter what
                            const bubble: BubbleRB = this.getBubbleFromCache(roomId) as BubbleRB;
                            if (!bubble?.bubbleConnectedUser) {
                                //refresh the bubble from the server
                                this.getServerBubble(roomId);
                            } else {
                                //check for user status, and update if necessary
                                bubble.bubbleConnectedUser.status = BubbleUserStatus.ACCEPTED;
                                this.updateMyUserToAcceptedStatus(bubble, true);
                            }
                        }, 500);
                    }

                    return true;
                }
                if (acknotification === 'true' || hidenotification === 'true') {
                    if (acknotification === 'true') {
                        this.logger.info(`${logMessage} acknotification`);
                        if (this.connectedUserPendingRequests[roomId]) this.connectedUserPendingRequests[roomId].ackRead = true;
                    }
                    if (hidenotification === 'true') {
                        this.logger.info(`${logMessage} hidenotification`);
                        delete this.connectedUserPendingRequests[roomId];
                    }

                    this.sendEvent(BubbleServiceEvents.ON_MY_USER_REQUESTS_TO_JOIN_BUBBLE_CHANGE);
                    return true;
                }

                if (bubble) {

                    // Handle room avatar
                    const avatarElem = stanzaElem.find('avatar');
                    const lastAvatarUpdateDate = roomElem.attr('lastAvatarUpdateDate');
                    const avatarType = (avatarElem.length > 0 && avatarElem.attr('action') === 'delete') ? 'delete' : 'update';

                    if (lastAvatarUpdateDate && avatarType === 'update') {
                        this.logger.info(`${logMessage} update avatar`);
                        bubble.lastAvatarUpdate = lastAvatarUpdateDate;
                        bubble.avatar = config.restServerUrl + "/api/room-avatar/" + bubble.dbId + "?size=512&update=" + MD5(lastAvatarUpdateDate);
                    }
                    else if (avatarType === 'delete') {
                        this.logger.info(`${logMessage} avatar removed`);
                        bubble.avatar = null;
                        //force reset of the list of contacts
                        bubble.avatarContacts = [];
                        bubble.updateAvatarInfo();
                    }

                    this.manageonBubbleUpdateConfigMessage(bubble, roomElem);
                }
                else {
                    //room does not exist
                    if (this.createBubbleInProgress || this.bubblesInProgress[roomId]) {
                        this.logger.info(`${logMessage} room ${roomId} does not exist but createRoomInProgress -- wait`);
                        if (this.createBubbleInProgress) await this.createBubbleInProgress;
                        if (this.bubblesInProgress[roomId]) await this.bubblesInProgress[roomId];
                        this.logger.info(`${logMessage} createRoomInProgress execute for ${roomId}`);
                        this.onBubbleUpdateConfigMessage(stanza);
                    }
                    else {
                        const roomJid = roomElem.attr('roomjid');
                        this.logger.info(`${logMessage} room does not exist, create new one with id ${roomId}`);
                        this.bubblesInProgress[roomId] = this.getServerBubble(roomId);
                        //we know the JID of the bubble, so block all calls to get room by jid too
                        if (roomJid) { this.getBubbleByJidPromises[roomJid] = this.bubblesInProgress[roomId]; }
                        try { await this.bubblesInProgress[roomId]; }
                        catch { /* do nothing, execute code after */ }

                        // send event that room has been created (with some errors or not)
                        this.sendEvent(BubbleServiceEvents.ON_BUBBLE_CREATED, { bubble: this.bubbles[roomId] });
                        this.logger.info(`${logMessage} room with id ${roomId} is now created`);
                        this.bubblesInProgress[roomId] = null;
                        delete this.bubblesInProgress[roomId];
                        if (this.getBubbleByJidPromises[roomJid]) { this.getBubbleByJidPromises[roomJid] = null; delete this.getBubbleByJidPromises[roomJid]; }
                    }
                }
            }

            return true;
        }
        catch (error) {
            this.logger.info("[bubbleService] onBubbleUpdateConfigMessage  -- failure -- " + error.message);
            return true;
        }
    }

    //API that is called from the room-container service to update the room containers in case of event from server
    //TO DO: We need to move this to the room-container service (and see how to use it ... option in the room start ??)
    public onRoomsContainerUpdate(stanza) {
        const stanzaElem = xmlParser(stanza);
        // Update containerId/containerName datas from Room objects
        const roomscontainerElem = stanzaElem.find("roomscontainer");
        if (stanzaElem.attr("type") === "management" && roomscontainerElem.length > 0) {
            const containerId = roomscontainerElem.attr("containerid");
            const containerName = roomscontainerElem.attr("name");
            const action = roomscontainerElem.attr("action");
            switch (action) {
                case `update`: {
                    const addedRooms: string[] = [];
                    const addedElem = roomscontainerElem.find("added");
                    if (addedElem.length) {
                        addedElem.find("roomid").forEach((roomIdElem) => { addedRooms.push(roomIdElem.text()); });
                        addedRooms.forEach((roomId) => {
                            if (this.bubbles[roomId]) {
                                this.bubbles[roomId].containerId = containerId;
                                this.bubbles[roomId].containerName = containerName;
                            }
                        });
                    }

                    const removedRooms: string[] = [];
                    const removedElem = roomscontainerElem.find("removed");
                    if (removedElem.length) {
                        removedElem.find("roomid").forEach((roomIdElem) => { removedRooms.push(roomIdElem.text()); });
                        removedRooms.forEach((roomId) => {
                            if (this.bubbles[roomId]) {
                                this.bubbles[roomId].containerId = ``;
                                this.bubbles[roomId].containerName = ``;
                            }
                        });
                    }
                    break;
                }
                case `create`: {
                    const addedRooms: string[] = [];
                    const addedElem = roomscontainerElem.find("added");
                    if (addedElem.length) {
                        addedElem.find("roomid").forEach((roomIdElem) => { addedRooms.push(roomIdElem.text()); });
                        addedRooms.forEach((roomId) => {
                            if (this.bubbles[roomId]) {
                                this.bubbles[roomId].containerId = containerId;
                                this.bubbles[roomId].containerName = containerName;
                            }
                        });
                    }
                    break;
                }
                case `delete`: {
                    const rooms = this.getRoomsByContainerId(containerId);
                    rooms.forEach((room: BubbleRB) => {
                        this.bubbles[room.dbId].containerId = ``;
                        this.bubbles[room.dbId].containerName = ``;
                    });
                }
                    break;
                default:
                    break;
            }
        }
    }

    private extractHtmlContent(html: string): string {
        const parser = new DOMParser();
        let content = "{}";
        try { content = parser.parseFromString(html, "text/html").documentElement.textContent; }
        catch (error) {
            this.logger.error("[bubbleService] error in extractHtmlContent:" + error.message);
            content = "{}";
        }
        return content;
    }

    private onBubblePresence(stanza: any) {
        try {
            const stanzaElem = xmlParser(stanza);
            const fromJid = stanzaElem.attr("from");
            const fromRoomBareJid = this.xmppService.getBareJidFromJid(fromJid);
            let fromUserJid = this.xmppService.getResourceFromJid(fromJid);

            // Get xmpp status
            const statusElems = stanzaElem.find("status");
            let statusCode = null;
            statusElems.forEach((statusElem) => {
                const code = statusElem.attr('code');
                if (code === '332') { statusCode = "332"; } // Disconnected
                if (code === '338') { statusCode = "338"; } // Deactivated
                if (code === '339') { statusCode = "339"; } // Reactivated
                // Do not set status code if already set
                if (code === "110" && !statusCode) { statusCode = "110"; }
            });

            // Get the associated room
            const room = this.getBubbleFromCache(fromRoomBareJid) as BubbleRB;
            if (room) {
                if (statusCode === "338") {
                    this.logger.info("[bubbleService] onBubblePresence - " + room.dbId + "- Deactivated");
                    room.isActive = false;
                    room.rxSubject.next(RBEvent.create("isActive"));
                }
                else if (statusCode === "339") {
                    this.logger.info("[bubbleService] onBubblePresence - " + room.dbId + "- Reactivated");
                    room.ready = false;
                    this.sendInitialRoomPresenceSync(room);
                    room.isActive = true;
                    room.rxSubject.next(RBEvent.create("isActive"));
                }
                else if (statusCode === "332") {
                    this.logger.info("[bubbleService] onBubblePresence - " + room.dbId + "- Room is stopped");
                    room.isActive = false;
                    room.rxSubject.next(RBEvent.create("isActive"));
                    //we need to have easy reconnection here for bubbles, in case of outage
                    //add progressive init room presence up to 1-2 min ?
                }
                else {
                    this.logger.info("[bubbleService] onBubblePresence -- " + room.dbId);
                    if (fromUserJid.indexOf("/") !== -1) {
                        fromUserJid = this.xmppService.getBareJidFromJid(fromUserJid);
                    }
                    // Handle presence from myself
                    if (this.contactService.isUserContactJid(fromUserJid)) {
                        const type = stanzaElem.attr("type");
                        if (type && type === "unavailable" && statusCode === "110") {
                            this.logger.warn("[bubbleService] disconnected from room " + room.getNameForLogs() + " because of a unavailable presence -- do nothing");
                        }
                        else if (statusCode === "332") {
                            this.logger.warn("[bubbleService] disconnected from room " + room.getNameForLogs() + " because of a system shutdown");
                            room.ready = false;
                            this.sendInitialRoomPresenceSync(room);
                        }
                        else {
                            room.ready = true;

                            //remove room from waiting list, if there
                            if (this.roomPresenceWaitingList?.length) {
                                this.roomPresenceWaitingList.forEach((waitingListRoom, index) => {
                                    if (waitingListRoom.dbId === room.dbId) {
                                        this.roomPresenceWaitingList.splice(index, 1);
                                    }
                                });
                            }
                            if (room.initPresPromise) {
                                this.logger.info("[bubbleService] onBubblePresence - initPresPromise resolved");
                                room.initPresPromiseResolve(room);
                                if (room.initPresInterval) {
                                    room.initPresInterval.unsubscribe();
                                    room.initPresInterval = null;
                                }
                                room.initPresPromise = null;
                                room.initPresPromiseResolve = null;
                                room.rxSubject.next(RBEvent.create("presenceRevolved"));
                            }
                        }
                    }

                    // Handle presence for other participant
                    else if (room.dbId && !this.bubbles[room.dbId]) {
                        this.getServerBubble(room.dbId);
                    }
                }
            }
            return true;
        }
        catch (error) {
            this.logger.error("[bubbleService] onBubblePresence error " + error);
            return true;
        }
    }

    // ------------------------------------------------------------
    //                      ROOM HELPER FUNCTIONS
    // ------------------------------------------------------------

    private updateUserStatus(bubble: BubbleRB, bubbleUser: BubbleUser, status) {

        // We immediately determine the event associated with the user's status in the bubble.
        // This event will only be sent at the end of status processing, except in special cases.
        let actionEvent: BubbleServiceEvents = this.getEventFromBubbleStatus(status); ;

        if (this.contactService.isUserContactJid(bubbleUser.user.jid)) {
            // Handle presence from myself
            this.logger.info("[bubbleService] Connected User status " + status);

            switch (status) {
                case "deleted":
                    bubbleUser.status = BubbleUserStatus.DELETED;
                    delete this.bubblesByJids[bubble.jid];
                    delete this.bubbles[bubble.dbId];
                    this.logger.info("[bubbleService] updateUserStatus --deleteRoom successfully (" + bubble.dbId + ")");

                    //remove my presence from the room
                    this.sendUnavailableRoomPresence(bubble);
                    this.fileStorageService?.removeRoomFileViewer(bubble);

                    if (this.contactService.userContact.guestMode && !bubble.isWebinarBubble()) {
                        this.eventService.publish("ON_OPEN_POPUP", "concludeInvitation");
                    }
                    // Clear conference stuff
                    // @DNI: added for CRRAINB-35019 but not sure if that's a good idea to add an eventService publish ...
                    if (bubble.isBubbleConferenceStarted()) {
                        //we need to leave any active conference just in case
                        const activeConference = this.webConferenceService?.getWebConferenceSessionById(bubble.dbId);
                        if (activeConference?.haveJoined) {
                            this.webConferenceService?.leaveWebConference(activeConference);
                        }
                        this.webConferenceService?.sendWebConferenceEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_ENDED, bubble);
                    }

                    //remove any pending access requests
                    if (this.connectedUserPendingRequests[bubble.dbId]) {
                        //manually add pending request, in case of guest
                        delete this.connectedUserPendingRequests[bubble.dbId];
                        this.sendEvent(BubbleServiceEvents.ON_MY_USER_REQUESTS_TO_JOIN_BUBBLE_CHANGE);
                    }

                    break;

                case "unsubscribed":
                    this.logger.info("[bubbleService] unsubscribed successfully (" + bubble.dbId + ")");
                    this.sendUnavailableRoomPresence(bubble);
                    bubbleUser.status = BubbleUserStatus.UNSUBSCRIBED;
                    bubble.updateAvatarInfo();
                    bubble.ready = false;
                    break;

                case "accepted":
                    // User has accepted the invitation
                    bubbleUser.status = BubbleUserStatus.ACCEPTED;
                    this.updateMyUserToAcceptedStatus(bubble, true);
                    // Reset the event action to send: Event ON_USER_INVITATION_ACCEPTED has already be sent in 'updateMyUserToAcceptedStatus'
                    actionEvent = undefined;
                    break;

                case "rejected":
                    // User has rejected the invitation
                    delete this.bubblesByJids[bubble.jid];
                    delete this.bubbles[bubble.dbId];
                    bubbleUser.status = BubbleUserStatus.REJECTED;
                    break;

                default:
                    break;
            }
            if (actionEvent) {
                this.sendEvent(actionEvent, { bubble: bubble, bubbleUser: bubbleUser });
            }
        }

        // Handle presence for other participant
        else {
            this.logger.info(`[bubbleService] Update user ${bubbleUser.user.dbId} with status ${status}`);
            if (bubbleUser.status === BubbleUserStatus.DELETED && status === BubbleUserStatus.ACCEPTED) {
                bubbleUser.date = new Date();

                //remove user's organizer rights
                if (bubbleUser.role === BubbleUserRole.ORGANIZER) {
                    bubbleUser.role = BubbleUserRole.USER;
                }
            }
            bubbleUser.status = status;
            bubble.updateAvatarInfo();
            this.refreshMemberAndOrganizerLists(bubble);

            switch (status) {
                case "invited":
                    // User has received an invitation to join the bubble
                    bubble.nbParticipants += 1;
                    // Reset the event action to send: Event ON_USER_INVITATION_RECEIVED must not be sent
                    // if it does not concern the connected user (no current interest for components/services) .
                    actionEvent = undefined;
                    break;

                case "accepted":
                    // User has accepted the invitation
                    break;

                case "rejected":
                    // User has rejected the invitation
                    bubble.nbParticipants -= 1;
                    break;

                case "deleted":
                    // User has been removed from the bubble by the owner or one of the moderators
                    bubble.nbParticipants -= 1;
                    break;

                case "unsubscribed":
                    // User has left the bubble of his own
                    bubble.nbParticipants -= 1;
                    break;

                default:
                    break;
            }

            if (actionEvent) {
                // We have to send the same event to both the bubble instance and the service
                // because we don't know how the ‘subscriber client’ listens to these events.
                const bubbleActionEvent: BubbleEvents = this.convertToBubbleEvents(actionEvent);
                bubble.sendEvent(bubbleActionEvent, bubbleUser);
                this.sendEvent(actionEvent, { bubble: bubble, bubbleUser: bubbleUser });
            }
        }
    }

    private async updateMyUserToAcceptedStatus(bubble: BubbleRB, updateFromServer: boolean = false) {
        // User has accepted the invitation
        bubble.bubbleConnectedUser.status = BubbleUserStatus.ACCEPTED;
        if (this.fileStorageService) this.fileStorageService.retrieveReceivedFilesForNewRoom(bubble.dbId);
        this.sendInitialRoomPresenceSync(bubble);
        bubble.updateAvatarInfo();

        // refresh the room for any updates between the invite and the accept, if necessary
        if (updateFromServer) {
            try {
                await this.updateRoomFromServer(bubble);
            }
            catch {
                this.logger.warn("[bubbleService] getServerRoom " + bubble.dbId + " failed");
            }
        }

        this.sendEvent(BubbleServiceEvents.ON_USER_INVITATION_ACCEPTED, { bubble: bubble, bubbleUser: bubble.bubbleConnectedUser });

        if (bubble.isBubbleConferenceStarted()) {
            bubble.sendEvent(BubbleEvents.ON_CONFERENCE_START_EVENT);
            this.webConferenceService?.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_STARTED_EVENT_IN_BUBBLE, { roomDbId: bubble.dbId });
            this.logger.info("[bubbleService] onBubbleEventMessage : conferenceAdd");
        }
    }

    // ------------------------------------------------------------
    //                      OPENINVITEID STUFF
    // ------------------------------------------------------------

    public async resetOpenInviteId(bubble: Bubble): Promise<string> {
        try {
            const url = `${this.portalURL}users/${this.contactService.userContact.dbId}/public-links/reset`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ roomId: bubble.dbId });
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }
            const openInviteData = (await response.json()).data;
            if (openInviteData?.openInviteId && openInviteData.roomId) {
                this.logger.info("[bubbleService] resetOpenInviteId success - openinviteId " + openInviteData.openInviteId + " bound to room " + bubble.dbId);
                this.openInviteIdByRoomId[openInviteData.roomId] = {};
                this.openInviteIdByRoomId[openInviteData.roomId].roomType = openInviteData.roomType;
                this.openInviteIdByRoomId[openInviteData.roomId].openInviteId = openInviteData.openInviteId;
                this.openInviteIdByRoomId[openInviteData.roomId].openInviteLink = openInviteData.invitationURL;
            }
            return openInviteData.invitationURL;
        }
        catch {
            const errorMessage = "resetOpenInviteId failure";
            this.logger.error("[contactService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    public async bindOpenInviteIdToBubble(bubble: Bubble): Promise<string> {
        try {
            const url = `${this.portalURL}users/${this.contactService.userContact.dbId}/public-links/bind`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ roomId: bubble.dbId });
            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) { throw (response); }
            const openInviteData = (await response.json()).data;

            if (openInviteData?.roomId && openInviteData.roomType && openInviteData.openInviteId) {
                this.logger.info(`[bubbleService] createRoomOpenInviteId -- SUCCESS - room ${openInviteData.roomId} with type ${openInviteData.roomType} bound to openInviteId ${openInviteData.openInviteId}`);
                this.openInviteIdByRoomId[openInviteData.roomId] = {};
                this.openInviteIdByRoomId[openInviteData.roomId].roomType = openInviteData.roomType;
                this.openInviteIdByRoomId[openInviteData.roomId].openInviteId = openInviteData.openInviteId;
                this.openInviteIdByRoomId[openInviteData.roomId].openInviteLink = openInviteData.invitationURL;
                return openInviteData.invitationURL;
            }
            throw (new Error(`[bubbleService] No or unvalid openInviteData`));
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'bindOpenInviteIdToBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async unbindOpenInviteFromBubble(bubble: Bubble) {
        try {
            const url = `${this.portalURL}users/${this.contactService.userContact.dbId}/public-links/unbind`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ roomId: bubble.dbId });
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw response; }
            this.logger.info("[bubbleService] openInvite successfully unbound");
            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'unbindOpenInviteFromBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async joinRoomUsingOpenInviteId(openInviteId: string) {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/rooms/open-invites`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ openInviteId });
            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) { throw response; }

            // Should we do something with "hasAlreadyJoinThisRoom" ?
            const openInvitesData = (await response.json()).data;
            if (openInvitesData?.roomId) return { bubbleId: openInvitesData.roomId, isWaitingInTheLobby: openInvitesData.isWaitingInTheLobby };
            throw new Error('No roomId info in openInvitation');
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'joinRoomUsingOpenInviteId');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async retrieveOpenInviteData(type: string = null, roomId: string = null) {
        try {
            let urlParams = "";
            if (roomId) { urlParams += "?roomId=" + roomId; }
            if (type) { urlParams += urlParams ? "&type=" + type : "?type=" + type; }
            const url = this.portalURL + "users/" + this.contactService.userContact.dbId + "/public-links" + urlParams;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }

            const openInviteData = (await response.json()).data;
            if (openInviteData?.length) {
                this.logger.info("[bubbleService] retrieveOpenInviteData - type: " + type + " - roomId: " + roomId + " -- SUCCESS - found " + openInviteData.length);
                openInviteData.forEach((openInvitePair: any) => {
                    if (openInvitePair.roomId) {
                        this.openInviteIdByRoomId[openInvitePair.roomId] = {};
                        this.openInviteIdByRoomId[openInvitePair.roomId].roomType = openInvitePair.roomType;
                        this.openInviteIdByRoomId[openInvitePair.roomId].openInviteId = openInvitePair.openInviteId;
                        this.openInviteIdByRoomId[openInvitePair.roomId].openInviteLink = openInvitePair.invitationURL;
                        // this.logger.info("[bubbleService] retrieveOpenInviteData - openInviteId " + openInvitePair.openInviteId + " set for room " + openInvitePair.roomId);
                    }
                });
            }
            else { this.logger.info("[bubbleService] retrieveOpenInviteData -- FAILURE - none found"); }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveOpenInviteData');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async retrieveOpenInviteIdForBubble(bubble: Bubble): Promise<string> {
        this.logger.debug("[bubbleService] retrieveOpenInviteIdForBubble - bubbleId: " + bubble.dbId);
        try {
            const url = `${this.portalURL}rooms/${bubble.dbId}/public-links`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }

            const openInviteData = (await response.json()).data;
            // store it
            if (openInviteData.openInviteId) {
                this.openInviteIdByRoomId[bubble.dbId] = {};
                this.openInviteIdByRoomId[bubble.dbId].roomType = "default";
                this.openInviteIdByRoomId[bubble.dbId].openInviteId = openInviteData.openInviteId;
                this.openInviteIdByRoomId[bubble.dbId].openInviteLink = openInviteData.invitationURL;
                this.logger.debug("[bubbleService] retrieveOpenInviteIdForBubble - openInviteId " + openInviteData.openInviteId + " set for bubble " + bubble.dbId);
            }
            return openInviteData.openInviteId;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveOpenInviteIdForBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async retrieveOpenInviteLinkForBubble(bubble: Bubble): Promise<string> {
        this.logger.debug("[bubbleService] retrieveOpenInviteLinkForBubble - bubbleId: " + bubble.dbId);
        try {
            const url = `${this.portalURL}rooms/${bubble.dbId}/public-links`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }

            const openInviteData = (await response.json()).data;
            // store it
            if (openInviteData.openInviteId) {
                this.openInviteIdByRoomId[bubble.dbId] = {};
                this.openInviteIdByRoomId[bubble.dbId].roomType = "default";
                this.openInviteIdByRoomId[bubble.dbId].openInviteId = openInviteData.openInviteId;
                this.openInviteIdByRoomId[bubble.dbId].openInviteLink = openInviteData.invitationURL;
                this.logger.debug("[bubbleService] retrieveOpenInviteLinkForBubble - openInviteLink " + openInviteData.openInviteLink + " set for bubble " + bubble.dbId);
            }
            return openInviteData.invitationURL;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveOpenInviteLinkForBubble');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method checks the validity of an openInviteId
     * @param openInvideId - to check
     */
    public async checkOpenInviteIdValidity(openInvideId: string): Promise<{ roomName: string; roomTopic: string; roomLocked: string; invitingFirstName: string; invitingLastName: string; invitingCompanyName: string; }> {
        this.logger.debug("[bubbleService] checkOpenInviteIdValidity - openInvideId: " + openInvideId);

        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/rooms/open-invites/validate?openInviteId=${openInvideId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }

            const openInviteData = (await response.json()).data;

            const roomName = openInviteData.roomName;
            const roomTopic = openInviteData.roomTopic;
            const roomLocked = openInviteData.roomLocked;
            const invitingFirstName = openInviteData.invitingFirstName;
            const invitingLastName = openInviteData.invitingLastName;
            const invitingCompanyName = openInviteData.invitingCompanyName;
            this.logger.info(`[bubbleService] checkOpenInviteIdValidity:\nroomName: ${roomName}\nroomTopic: ${roomTopic}\nroomLocked: ${roomLocked}\ninvitingFirstName: ${invitingFirstName}\ninvitingLastName: ${invitingLastName}\ninvitingCompanyName: ${invitingCompanyName}`);
            return openInviteData;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'checkOpenInviteIdValidity');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public getOpenInviteIdLinkForRoom(roomId: string) {
        if (this.openInviteIdByRoomId[roomId]) { return this.openInviteIdByRoomId[roomId].openInviteLink; }
        return null;
    }

    public getOpenInviteIdForRoom(roomId: string) {
        if (this.openInviteIdByRoomId[roomId]) { return this.openInviteIdByRoomId[roomId].openInviteId; }
        return null;
    }

    /***
    * Method retrieve the room consumption of a given user (API request to https://api.openrainbow.org/enduser/#api-rooms-getUsersConsumption Rainbow End User portal - rooms - User Quota and Consumption)
    */
    public async retrieveRoomConsumption() {
        this.logger.debug(`[bubbleService] retrieveRoomConsumption`);
        try {
            const url = `${this.portalURL}rooms/consumption`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            const roomConsumptionData = (await response.json()).data;

            this.logger.debug(`[bubbleService] retrieveRoomConsumption -- SUCCESS -- ${roomConsumptionData.currentValue}`);
            return roomConsumptionData.currentValue;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveRoomConsumption');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async startAdHocConference(caller: any, participants: any) {
        try {
            const url = `${this.portalURL}ad-hoc`;
            const headers = this.authService.getPostHeader();
            const bodyData: any = {};
            if (caller) {
                bodyData.caller = caller;
            }
            bodyData.participants = participants;
            if (this.webConferenceService) { this.webConferenceService.adhocConferenceStarting = true; }

            const response: Response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(bodyData) });
            if (!response.ok) { throw (response); }
            const roomData = (await response.json()).data;

            if (this.webConferenceService) { this.webConferenceService.adhocConferenceStarting = false; }
            this.logger.debug(`[bubbleService] startAdHocConference -- SUCCESS -- `);
            return roomData;
        }
        catch (error) {
            if (this.webConferenceService) { this.webConferenceService.adhocConferenceStarting = false; }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'startAdHocConference');
            this.logger.error(`[bubbleService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Helper API that is used to get the participant object from the CALL (PSTN or WebRTC) that can be used to create Ad-hoc conference via startAdHocConference API
     * @param call - The active call to conference: this call must have a status ACTIVE (it could be a PSTN call or WebRTC call)
     * @returns  object with 2 properties, participant object that should be send to the startAdHocConference and blindTrasfer boolean if the call also needs to
     * be redirected to the bubble via Blind Transfer API
     */
    public getParticipantDataForAdhocFromCall(call: CallRB) {
        const result = {
            participant: null,
            blindTrasfer: false
        };

        //build participant list to send to the ADDHOC API in the room (via ad-hoc or MERGE calls)
        if (call.callType === CallType.PHONE) {
            let deskphoneCase = false;
            if (call.sipWiseEndPointDeviceList?.length
                && call.id === call.sipWiseEndPointDeviceList[0].callId) {
                deskphoneCase = (call.sipWiseEndPointDeviceList[0].deviceType !== "webrtc"); //CRRAINB-36312 inverse logic
            } else {
                //CRRAINB-36312 when no info (sip generic case ) use blindTransfer as default
                // meaning deskphoneCase otherwise a deviceType = "webrtc" would have been received
                // that is conveniant because Adhoc conf only proposed with active call (so device is known)
                deskphoneCase = true;
            }

            //if contact is a PSTN number (temporary contact)
            if (call.contact.temp) {
                result.participant = { phoneNumber: call.contact.id, connected: true };
                result.blindTrasfer = true;
            }
            else if (call.sipWiseData?.called?.phoneNumber && call.sipWiseData.called.phoneNumber.indexOf("+") !== -1) {
                result.participant = { phoneNumber: call.sipWiseData.called.phoneNumber, connected: true };
                result.blindTrasfer = true;
            }
            else if (!call.sipWiseEndPointDeviceList?.length) {
                //list of endpoint device is empty
                result.participant = { phoneNumber: call.remoteParty?.phoneNumber, connected: true };
                result.blindTrasfer = true;
            }
            else {
                result.participant = { userId: call.contact.dbId, connected: true, deskphone: deskphoneCase };
                if (deskphoneCase) { result.blindTrasfer = true; }
            }
        } else {
            result.participant = { userId: call.contact.dbId, connected: true };
        }

        let typeInfo: string = "Undefined";
        switch (call.callType) {
            case CallType.WEBRTC:
                typeInfo = "Video";
                break;
            case CallType.PHONE:
                typeInfo = "Phone";
                break;
            default: break;
        }
        this.logger.info(`[bubbleService] getParticipantDataForAdhocFromCall for call ${call.id}: type ${typeInfo} -- ${JSON.stringify(result.participant)} -- blind transfer : ${result.blindTrasfer}`);

        return result;
    }

    private getLanguageWithIsoCode(): string {
        let result = "en";
        if (this.contactService.userContact.language) {
            const splitted = this.contactService.userContact.language.split("-");
            if (splitted.length > 1) {
                result = splitted[0] + "-" + splitted[1].toUpperCase();
            } else {
                result = splitted[0];
            }
        }
        return result;
    }

    public bubbleLogger(log: string) {
        this.logger.error(`[BubbleModel] ${log}`);
    }

    // Crer une methode private pour rendre event à envoyer en fonction de status
    private getEventFromBubbleStatus(status: string): BubbleServiceEvents | undefined {
        switch (status) {
            case "invited":
                // User has received an invitation to join the bubble
                return BubbleServiceEvents.ON_USER_INVITATION_RECEIVED;

            case "accepted":
                // User has accepted the invitation
                return BubbleServiceEvents.ON_USER_INVITATION_ACCEPTED;

            case "rejected":
                // User has rejected the invitation
                return BubbleServiceEvents.ON_USER_INVITATION_DECLINED;

            case "deleted":
                // User has been removed from the bubble by the owner or one of the moderators
                return BubbleServiceEvents.ON_USER_BUBBLE_REMOVED;

            case "unsubscribed":
                // User has left the bubble of his own
                return BubbleServiceEvents.ON_USER_BUBBLE_LEFT;

            default:
                this.logger.info(`[bubbleService] getEventFromBubbleStatus -- Unknown specified bubble user status ${status}`);
                return undefined;
        }
    }

    private convertToBubbleEvents(bubbleServiceEvents: BubbleServiceEvents): BubbleEvents | undefined {

        switch (bubbleServiceEvents) {
            case BubbleServiceEvents.ON_USER_INVITATION_ACCEPTED:
                return BubbleEvents.ON_USER_INVITATION_ACCEPTED;

            case BubbleServiceEvents.ON_USER_INVITATION_DECLINED:
                return BubbleEvents.ON_USER_INVITATION_DECLINED;

            case BubbleServiceEvents.ON_USER_BUBBLE_LEFT:
                return BubbleEvents.ON_USER_BUBBLE_LEFT;

            case BubbleServiceEvents.ON_USER_BUBBLE_REMOVED:
                return BubbleEvents.ON_USER_BUBBLE_REMOVED;

            default:
                this.logger.info(`[bubbleService] convertToBubbleEvents -- Unknown specified bubble service event ${bubbleServiceEvents}`);
                return undefined;
        }
    }
}
